/* ============================================================================
 * shoppable-promotions.js  —  client engine for the NEW feed-driven IB
 * shoppable-promotions section.
 *
 * HC - Shoppable promotions: client engine - 2026-06-29
 *
 * Browser-global IIFE (window.ShoppablePromotions). No build step: raw,
 * ES5-ish, browser-global JS (no imports/modules/JSX). Defer-safe — there is
 * NO top-level DOM work; init() runs on DOMContentLoaded (or immediately if the
 * document is already parsed). Loaded by layout/theme.liquid AFTER the deferred
 * collection-atc.js / theme-cart.js chain on the promo template suffix, so the
 * reuse contracts (window.CollectionATC, window.theme.cart, window.IBAccessPolicy)
 * exist by the time anything here runs.
 *
 * Pipeline:  Feed -> Resolve -> Gating -> CardGrid -> Filters -> Modal
 *            -> CollectionGrid(reinit) -> BuyBox(product) -> Cart
 *
 * The feed is treated as UNTRUSTED: escapeHtml() / safeHref() are applied to
 * every interpolated promo field (title/brand/image/badge/description/terms/
 * included.label/handle). encodeURIComponent() wraps every handle in a fetch
 * URL (at least one live handle is non-ASCII, e.g. neu-moisture®-instant-fix).
 *
 * Cart writes go ONLY through window.theme.cart (never raw /cart/add.js).
 * Bundle/product adds use theme.cart.addSilently(...) with
 * properties:{ _promo_id } so the line keeps its promo attribution.
 *
 * See promo-section-build-spec.md §3.3 / §4 / §5 / §6 / §7.
 * ==========================================================================*/

window.ShoppablePromotions = (function () {
  'use strict';

  var Feed = window.IBPromoFeed;
  var MISSING_FEED_MESSAGE = 'Promotions feed module could not be loaded.';
  function missingFeedHelper() {
    throw new Error(MISSING_FEED_MESSAGE);
  }
  var feedReady = !!(Feed &&
    typeof Feed.stripDollars === 'function' &&
    typeof Feed.resolveSource === 'function' &&
    typeof Feed.ymdLabel === 'function' &&
    typeof Feed.rangeLabel === 'function' &&
    typeof Feed.normalizeFeed === 'function' &&
    typeof Feed.isPromoLive === 'function');
  if (!feedReady && typeof console !== 'undefined' && console && typeof console.error === 'function') {
    console.error('[ShoppablePromotions] window.IBPromoFeed is missing. Load promotions-feed.js before shoppable-promotions.js.');
  }
  if (!feedReady) {
    Feed = {
      stripDollars: missingFeedHelper,
      resolveSource: missingFeedHelper,
      ymdLabel: missingFeedHelper,
      rangeLabel: missingFeedHelper,
      normalizeFeed: missingFeedHelper,
      isPromoLive: missingFeedHelper
    };
  }
  var stripDollars = Feed.stripDollars;
  var resolveSource = Feed.resolveSource;
  var ymdLabel = Feed.ymdLabel;
  var rangeLabel = Feed.rangeLabel;
  var normalizeFeed = Feed.normalizeFeed;
  var isPromoLive = Feed.isPromoLive;

  /* ----------------------------------------------------------------------
   * 0. Small utilities
   * -------------------------------------------------------------------- */

  // Retry backoff for Section-Rendering fetches (mirrors color-grid.liquid).
  var RETRY_BACKOFF = [1000, 2000, 4000];

  function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Allow only http(s), protocol-relative, root-relative and CDN URLs; reject
  // javascript:, data:, vbscript: and other script-bearing schemes. Returns a
  // safe href string ('#' when the input is unusable).
  function safeHref(value) {
    if (!value) return '#';
    var raw = String(value).trim();
    // Strip control chars that could smuggle a scheme past the test.
    var probe = raw.replace(/[\x00-\x1f\x7f\s]/g, '').toLowerCase();
    if (probe.indexOf('javascript:') === 0) return '#';
    if (probe.indexOf('data:') === 0) return '#';
    if (probe.indexOf('vbscript:') === 0) return '#';
    if (/^[a-z][a-z0-9+.-]*:/.test(probe)) {
      // Has an explicit scheme — only permit http / https / mailto / tel.
      if (!/^(https?:|mailto:|tel:)/.test(probe)) return '#';
    }
    return raw;
  }

  // Decode HTML character references enough to defeat entity-smuggled schemes
  // (e.g. `&#106;avascript:` -> `javascript:`) BEFORE a value reaches safeHref.
  // Single decode pass, matching how a browser decodes an attribute value once.
  function spCodePoint(n) {
    if (isNaN(n) || n <= 0 || n > 0xffff) return '';
    return String.fromCharCode(n);
  }
  function decodeEntities(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&#x([0-9a-f]+);?/gi, function (_m, h) { return spCodePoint(parseInt(h, 16)); })
      .replace(/&#(\d+);?/g, function (_m, d) { return spCodePoint(parseInt(d, 10)); })
      .replace(/&(amp|lt|gt|quot|apos|colon|tab|newline|sol);/gi, function (_m, n) {
        var map = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", colon: ':', tab: '\t', newline: '\n', sol: '/' };
        return map[String(n).toLowerCase()] || _m;
      });
  }

  // Render feed rich-text through a TAG ALLOWLIST (the feed is untrusted per
  // spec §0). Every tag NOT on RICHTEXT_TAGS is dropped entirely (its inner text
  // is kept) — this kills <img>/<svg>/<script>/etc. On the allowed tags ALL
  // attributes are stripped except href on <a>, which is entity-decoded and run
  // through safeHref. So `<img src=x/onerror=…>` (slash-separated handler on a
  // non-allowlisted tag) and `<a href="&#106;avascript:…">` (entity-encoded
  // scheme) are both neutralized, regardless of attribute separator. No
  // DOMParser: this hardened string sanitizer ships to browsers AND runs
  // unchanged inside the node:vm test sandbox.
  var RICHTEXT_TAGS = { strong: 1, em: 1, b: 1, i: 1, u: 1, br: 1, p: 1, span: 1, a: 1, ul: 1, ol: 1, li: 1 };
  function richText(html) {
    if (!html) return '';
    var tags = [];
    var text = String(html).replace(/\u0000/g, '').replace(/<[^>]*>/g, function (tag) {
      var m = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)([\s\S]*?)\/?\s*>$/.exec(tag);
      if (!m) return ''; // comment / doctype / malformed -> drop
      var name = m[2].toLowerCase();
      if (!RICHTEXT_TAGS[name]) return ''; // non-allowlisted tag -> drop, keep text
      var safeTag;
      if (m[1] === '/') {
        safeTag = '</' + name + '>';
        tags.push(safeTag);
        return '\u0000' + (tags.length - 1) + '\u0000';
      }
      if (name === 'a') {
        var hm = /\bhref\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i.exec(m[3] || '');
        if (hm) {
          var val = hm[1].replace(/^["']|["']$/g, '');
          safeTag = '<a href="' + escapeHtml(safeHref(decodeEntities(val))) + '">';
          tags.push(safeTag);
          return '\u0000' + (tags.length - 1) + '\u0000';
        }
        safeTag = '<a>';
      } else {
        safeTag = '<' + name + '>'; // all attributes (incl. on*-handlers) dropped
      }
      tags.push(safeTag);
      return '\u0000' + (tags.length - 1) + '\u0000';
    });
    text = text.replace(/\bon[a-z0-9-]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '');
    text = text.replace(/</g, '&lt;');
    return text.replace(/\u0000(\d+)\u0000/g, function (_m, idx) {
      return tags[parseInt(idx, 10)] || '';
    });
  }

  // Stable 0/1 parity from a promo id — varies the stage field colour per promo.
  function spHash(s) { var h = 0, i; s = String(s || ''); for (i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; }

  // Repeating "wave" pattern for the product stage (mock-up): dark strokes on
  // sage, light strokes on rouge. Single quotes are %27-encoded so the data URI
  // is safe inside a JS-assigned style.backgroundImage.
  function waveUrl(rouge) {
    var stroke = rouge ? 'rgba(239,233,225,0.16)' : 'rgba(20,20,20,0.10)';
    var svg = "<svg xmlns='http://www.w3.org/2000/svg' width='90' height='34'>" +
      "<g fill='none' stroke='" + stroke + "' stroke-width='1.4'>" +
      "<path d='M0 17 Q22 6 45 17 T90 17'/><path d='M0 8 Q22 -3 45 8 T90 8'/>" +
      "<path d='M0 26 Q22 15 45 26 T90 26'/></g></svg>";
    return 'url("data:image/svg+xml,' + encodeURIComponent(svg).replace(/'/g, '%27') + '")';
  }

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }
  function $all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }
  function keyMap() {
    return Object.create(null);
  }

  // kebab slug used for tag accent classes (matches the mock-up `slug()`).
  function slug(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* ----------------------------------------------------------------------
   * 1. Pricing-framing helpers (IB rule: NO dollar amounts on cards)
   *    The v1/v2 feed badges sometimes carry literal dollars ("Save $17.25").
   *    These MUST never reach a card — strip price tokens; prefer percent /
   *    free-gift framing; fall back to a neutral label.
   * -------------------------------------------------------------------- */
  function cleanFraming(str) {
    if (!str) return '';
    var s = String(str);
    var hasPrice = /\$|\d+\.\d{2}/.test(s);
    if (hasPrice) {
      if (/%/.test(s)) {
        // Keep just the percent clause (dollar-free).
        var m = s.match(/[^.;,]*\d+\s*%[^.;,]*/);
        s = m ? m[0] : s.replace(/\$\s?\d[\d.,]*/g, '');
      } else {
        s = s.replace(/\$\s?\d[\d.,]*/g, '');
      }
    }
    s = s.replace(/\s{2,}/g, ' ').replace(/\s*[-–—:]\s*$/, '').trim();
    // A bare leftover connector after stripping a price ("Save", "Only") is noise.
    if (/^(save|only|just|now)$/i.test(s)) return '';
    return s;
  }

  // Short badge chip text (percent-preferring, dollar-free, may be '').
  function badgeText(promo) {
    var a = cleanFraming(promo.badge);
    var b = cleanFraming(promo.discountLabel);
    if (a && /%|free|bogo|gift|buy/i.test(a)) return a;
    if (b && /%|free|bogo|gift|buy/i.test(b)) return b;
    return a || b || '';
  }

  // Body framing line (display-only, dollar-free, may be '').
  function framingText(promo) {
    return cleanFraming(promo.discountLabel) || cleanFraming(promo.badge) || '';
  }

  /* ----------------------------------------------------------------------
   * 2. Feed load + normalization (v2-only — see resolveSource; no v1 parsing)
   * -------------------------------------------------------------------- */

  // "What's included" quantity: parse a trailing "xN"/"×N" token off a note
  // ("LOR231050 x5" -> 5). Quantity-less notes (kit-parent SKUs like
  // "LORM226JUL02-K", plain SKUs) return 0 so the count can tell an explicit
  // quantity apart from a descriptor row.
  function includedNoteQty(note) {
    if (!note) return 0;
    var m = String(note).match(/(?:^|\s)[x×]\s*(\d+)\s*$/i);
    var n = m ? parseInt(m[1], 10) : 0;
    return n > 0 ? n : 0;
  }

  // Long promo titles shrink in the modal (see .rs-title--long) so they don't
  // wrap to three lines and dominate. Short/typical titles keep the large size.
  function titleClass(title) {
    return (title && title.length > 40) ? 'rs-title rs-title--long' : 'rs-title';
  }

  function fetchFeed(cfg) {
    var url = cfg.feedUrl;
    if (!url) return Promise.reject(new Error('No feed URL configured.'));
    return fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('Feed HTTP ' + res.status);
      return res.json();
    });
  }

  /* ----------------------------------------------------------------------
   * 3. Gating  (consume window.IBAccessPolicy — never re-implement)
   * -------------------------------------------------------------------- */

  // Map a promo's display tags to the "categories" the hair-color branch keys
  // on. IBAccessPolicy.getPromotionCategories is NOT exported on the policy, so
  // we hand evaluatePromotionAccess our own resolver via settings (it honours
  // settings.getPromotionCategories first — see product-restrictions.js:485).
  function getPromotionCategories(promo) {
    return (promo && Array.isArray(promo.tags)) ? promo.tags : [];
  }

  function evaluatePromo(promo, customerState, cfg) {
    var policy = window.IBAccessPolicy;
    if (!policy || typeof policy.evaluatePromotionAccess !== 'function') {
      // Normally fail OPEN (matches enable_pricing_restrictions=off). But when the
      // merchant EXPLICITLY enabled pricing restrictions, a missing policy engine
      // must NOT silently expose gated promos -> fail CLOSED with a locked-card
      // shape (matches what a restricted promo produces downstream). This is
      // intentionally stricter than the theme's fail-open convention, ONLY when
      // the setting is on.
      if (cfg && cfg.pricingRestrictionsEnabled) {
        return { allowed: false, restrictionType: 'not_logged_in', message: null };
      }
      return { allowed: true, restrictionType: null, message: null };
    }
    return policy.evaluatePromotionAccess(promo, customerState, {
      pricingRestrictionsEnabled: cfg.pricingRestrictionsEnabled,
      getPromotionCategories: getPromotionCategories
    });
  }

  function evaluateProduct(productTags, customerState, isLoggedIn, restrictionsEnabled) {
    var policy = window.IBAccessPolicy;
    if (!policy || typeof policy.evaluateProductAccess !== 'function') {
      // Fail OPEN normally; fail CLOSED when pricing restrictions are enabled but
      // the policy engine is missing (same intentional strictness as evaluatePromo).
      if (restrictionsEnabled) {
        return { canViewPrice: false, canAddToCart: false };
      }
      return { canViewPrice: true, canAddToCart: true };
    }
    return policy.evaluateProductAccess(productTags, customerState, isLoggedIn);
  }

  /* ----------------------------------------------------------------------
   * 4. Inline SVG snippets (kept tiny; aria-hidden)
   * -------------------------------------------------------------------- */
  var SVG = {
    bag: '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true"><path d="M3 4 H13 L12 12 H4 Z" stroke="currentColor" stroke-width="1.2"/><path d="M6 4 V3 A2 2 0 0 1 10 3 V4" stroke="currentColor" stroke-width="1.2"/></svg>',
    plus: '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M6 2 V10 M2 6 H10" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
    minus: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>',
    inc: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
    close: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    clock: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>',
    arrow: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    check: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
    gift: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="9" width="18" height="12" rx="1"/><path d="M3 13h18M12 9v12M12 9S10 4 7.5 5 9 9 12 9M12 9s2-5 4.5-4S15 9 12 9"/></svg>',
    bottle: '<svg width="20" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.5h4v2.5h-4z"/><path d="M9.5 5h5v2.6l1 2.2V20a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V9.8l1-2.2z"/><path d="M8.5 14h7"/></svg>',
    filter: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>'
  };

  // Small checkmark reused by the .bm-row checkbox cells (brand + collection filters).
  var CHECK_SVG = '<svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true"><path d="M2 6l3 3 5-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function setFacetRowState(row, on) {
    row.classList.toggle('on', on);
    row.setAttribute('aria-pressed', on ? 'true' : 'false');
    var check = $('.bm-check', row);
    if (check) check.innerHTML = on ? CHECK_SVG : '';
  }

  /* ----------------------------------------------------------------------
   * 5. Urgency chip (derived from dates.end ISO — display only)
   * -------------------------------------------------------------------- */
  function urgencyChipHtml(promo) {
    var endIso = promo.dates && promo.dates.end;
    if (!endIso) {
      return '<span class="pc-urgency pc-urgency-soft">While supplies last</span>';
    }
    var end = new Date(endIso + 'T23:59:59');
    if (isNaN(end.getTime())) {
      return '<span class="pc-dates">' + escapeHtml(promo.dates.display) + '</span>';
    }
    var days = Math.ceil((end.getTime() - Date.now()) / 86400000);
    // Date-gated ends are pinned to T23:59:59, so live promos yield days >= 1;
    // days === 0 only occurs after end, and this sidesteps Math.ceil's -0.
    if (days <= 0) {
      if (promo.dates && promo.dates.whileSuppliesLast) return '<span class="pc-urgency pc-urgency-soft">While supplies last</span>';
      return '<span class="pc-urgency pc-urgency-ended">Ended</span>';
    }
    if (days <= 7) {
      var label = days <= 1 ? 'Last day' : (days + ' days left');
      return '<span class="pc-urgency pc-urgency-hot"><span class="pc-urgency-dot"></span>' + label + '</span>';
    }
    if (days <= 30) return '<span class="pc-urgency pc-urgency-warm">Ends in ' + days + ' days</span>';
    var nice = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return '<span class="pc-urgency pc-urgency-soft">Ends ' + escapeHtml(nice) + '</span>';
  }

  /* ----------------------------------------------------------------------
   * 6. Card markup builders
   * -------------------------------------------------------------------- */

  function badgeHtml(promo, badgeStyle) {
    var txt = badgeText(promo);
    if (!txt) return '';
    var accent = '';
    // Tag-driven accent: hair-color promos lean rouge, hair-care lean save.
    var t = (promo.tags || []).map(function (x) { return String(x).toLowerCase(); });
    if (t.indexOf('hair color') !== -1) accent = ' style="--badge-accent:var(--price)"';
    var safe = escapeHtml(txt);
    if (badgeStyle === 'bold') return '<span class="pb pb-bold"' + accent + '><span>' + safe + '</span></span>';
    if (badgeStyle === 'sticker') return '<span class="pb pb-sticker"' + accent + '><span>' + safe + '</span></span>';
    return '<span class="pb pb-medium"' + accent + '>' + safe + '</span>';
  }

  function tagsHtml(promo) {
    if (!promo.tags || !promo.tags.length) return '';
    var out = promo.tags.map(function (t) {
      return '<span class="pc-tag pc-tag-' + slug(t) + '">' + escapeHtml(t) + '</span>';
    }).join('');
    return '<div class="pc-tags">' + out + '</div>';
  }

  // CTA region per source type — a single thin button that just opens the modal
  // (data-sp-open bubbles to the card click -> openModal). Shown in every density.
  // Links have no modal, so they keep the direct link-out.
  function ctaHtml(promo, resolved, density) {
    if (resolved.type === 'collection') {
      return '<button type="button" class="pc-cta pc-add pc-shop-col" data-sp-open>' +
        SVG.bag + 'Shop Collection</button>';
    }
    if (resolved.type === 'product') {
      return '<button type="button" class="pc-cta pc-add pc-shop-col" data-sp-open>' + SVG.bag + 'Shop Deal</button>';
    }
    if (resolved.type === 'link') {
      return '<a class="pc-cta pc-add" href="' + escapeHtml(safeHref(resolved.fallbackLink)) + '" data-sp-stop>View Offer</a>';
    }
    return '';
  }

  function cardHtml(promo, resolved, access, index, cfg, grouped) {
    var density = cfg.density || 'compact';
    var compact = density === 'compact';
    var roleAttr = grouped ? '' : ' role="listitem"';
    var img = promo.image
      ? '<img src="' + escapeHtml(safeHref(promo.image)) + '" alt="' + escapeHtml(promo.title) + '" loading="lazy">'
      : '';

    // -------- LOCKED card (spec C1) — denied, never omitted. --------
    if (access && access.allowed === false) {
      var msg = access.message || 'Sign in to your pro account to view this promotion.';
      return '<article class="pc pc-' + density + ' pc--locked" data-sp-index="' + index +
        '"' + roleAttr + ' aria-label="' + escapeHtml(promo.title) + ' (locked)">' +
        '<div class="pc-img">' + img + badgeHtml(promo, cfg.badgeStyle) +
        '<span class="pc-lock">' + SVG.lock + '</span></div>' +
        '<div class="pc-body">' +
        '<div class="pc-brand">' + escapeHtml(promo.brand) + '</div>' +
        '<h3 class="pc-title">' + escapeHtml(promo.title) + '</h3>' +
        '<p class="pc-locked-msg">' + escapeHtml(msg) + '</p>' +
        '<div class="pc-locked-actions">' +
        '<a class="pc-locked-btn pc-locked-btn--primary" href="/account/login">Log in</a>' +
        '<a class="pc-locked-btn" href="/pages/contact">Contact us</a>' +
        '</div></div></article>';
    }

    // -------- Standard card --------
    var dateBlock = (cfg.urgency === 'countdown')
      ? urgencyChipHtml(promo)
      : '<div class="pc-dates">' + escapeHtml(promo.dates.display) + '</div>';

    var parts = [];
    // Grouped mode suppresses the spotlight treatment (the pc-featured card is
    // grid-column:span 2 and would overflow a nested brand-group grid). The
    // per-brand header hierarchy carries the emphasis instead.
    var spotlight = promo.featured && cfg.spotlight && !grouped;
    parts.push('<article class="pc pc-' + density + (spotlight ? ' pc-featured' : '') +
      '" data-sp-index="' + index + '" tabindex="0"' + roleAttr + ' aria-label="' + escapeHtml(promo.title) + '">');
    parts.push('<div class="pc-img">' + img + badgeHtml(promo, cfg.badgeStyle) + '</div>');
    parts.push('<div class="pc-body">');
    if (spotlight) parts.push('<div class="pc-flag">Featured</div>');
    parts.push('<div class="pc-brand">' + escapeHtml(promo.brand) + '</div>');
    parts.push('<h3 class="pc-title">' + escapeHtml(promo.title) + '</h3>');
    if (promo.subtitle && !compact) parts.push('<p class="pc-subtitle">' + escapeHtml(promo.subtitle) + '</p>');
    parts.push(dateBlock);
    if (!compact) parts.push(tagsHtml(promo));
    parts.push(ctaHtml(promo, resolved, density));
    parts.push('</div></article>');
    return parts.join('');
  }

  /* ----------------------------------------------------------------------
   * 7. The engine instance
   * -------------------------------------------------------------------- */
  function Engine(root) {
    this.root = root;
    this.cfg = null;
    this.customerState = null;
    this.promos = [];          // normalized + resolved + access-evaluated
    this.filtered = [];
    this.filters = { search: '', brands: keyMap(), categories: keyMap(), sort: 'featured' };
    this.grid = $('#sp-grid', root);
    this.status = $('#sp-status', root);
    this.modalRoot = $('#sp-modal-root', root);
    this.lastFocus = null;
    this.challengeBound = false;
    this.brandFitBound = false;
  }

  Engine.prototype.readConfig = function () {
    var node = $('#sp-config', this.root) || document.getElementById('sp-config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  };

  Engine.prototype.init = function () {
    if (!feedReady) { this.renderError(MISSING_FEED_MESSAGE); return; }
    var cfg = this.readConfig();
    if (!cfg) { this.renderError('Configuration error.'); return; }
    this.cfg = cfg;
    // customerState handed straight to IBAccessPolicy (it normalizes objects).
    this.customerState = cfg.customer || null;

    var self = this;
    // No feed configured (empty feedUrl — the shell resolved no source): render a
    // neutral empty state rather than the "something went wrong" error UI.
    if (!cfg.feedUrl) { this.renderEmpty('No promotions are available right now.'); return; }
    // On failure, surface a retryable error state.
    fetchFeed(cfg).then(function (json) {
      self.ingest(normalizeFeed(json));
    }).catch(function () {
      self.renderError('Promotions could not be loaded.');
    });
  };

  Engine.prototype.ingest = function (promos) {
    var self = this;
    // Date-gate: show a promo only within its [start, end] window. Bypassed in
    // "Show Future Promotions" (testing) mode so upcoming promos preview.
    var now = Date.now();
    var showFuture = !!(this.cfg && this.cfg.showFuturePromotions);
    this.promos = promos.filter(function (p) {
      return showFuture || isPromoLive(p, now);
    }).map(function (p) {
      return {
        promo: p,
        resolved: p.source,
        access: evaluatePromo(p, self.customerState, self.cfg)
      };
    });
    this.buildFilterControls();
    this.applyFilters();
    this.bindGlobalEvents();
    this.bindChallenge();
    this.populateMarquee();
    this.setupStickyFilters();
    this.setupBrandTitleFit();
  };

  // Sticky filter toolbar: pin it BELOW the sticky theme header (offset by its
  // height), and collapse to a thin search/filter bar — hiding the result count
  // + sort — once it's stuck.
  Engine.prototype.setupStickyFilters = function () {
    var filters = $('[data-sp-filters]', this.root);
    if (!filters || filters.getAttribute('data-sp-sticky-init')) return;
    filters.setAttribute('data-sp-sticky-init', '1');

    // The theme header has a full state (nav visible) and a compact state
    // (burger). Track the VISIBLE header bar (.site-header) rather than its
    // wrapper (.header-section), which keeps a full-height placeholder even when
    // the bar collapses — using the wrapper would leave a gap. Re-measured on
    // scroll so the offset follows the full->compact transition.
    var header = document.querySelector('.site-header') || document.querySelector('.header-section');
    var sentinel = document.createElement('div');
    sentinel.className = 'sp-filters-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    if (filters.parentNode) filters.parentNode.insertBefore(sentinel, filters);

    var off = 0, applied = -1;
    function sync() {
      // Bottom edge of the visible (pinned) header in viewport coords; clamps to
      // 0 once the header has scrolled away / isn't pinned.
      off = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      if (off !== applied) { filters.style.setProperty('--sp-filters-top', off + 'px'); applied = off; }
      filters.classList.toggle('is-stuck', sentinel.getBoundingClientRect().top < off + 1);
    }
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { sync(); ticking = false; });
    }
    sync();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  };

  /* ---------- Status / error surfaces ---------- */
  Engine.prototype.hideStatus = function () {
    if (this.status) this.status.hidden = true;
  };
  Engine.prototype.renderError = function (message) {
    if (this.grid) this.grid.setAttribute('aria-busy', 'false');
    if (!this.status) return;
    this.status.hidden = false;
    this.status.innerHTML =
      '<div class="sp-status__title">Something went wrong</div>' +
      '<p class="sp-status__msg">' + escapeHtml(message) + '</p>' +
      '<button type="button" class="sp-retry-btn" data-sp-retry>Try again</button>';
    var self = this;
    var retry = $('[data-sp-retry]', this.status);
    if (retry) retry.addEventListener('click', function () {
      self.status.innerHTML = '<div class="sp-spinner" aria-hidden="true"></div><p class="sp-status-text">Loading promotions…</p>';
      self.init();
    });
  };

  // Neutral empty state (no feed configured / nothing to show) — distinct from
  // renderError's "something went wrong" + retry UI.
  Engine.prototype.renderEmpty = function (message) {
    if (this.grid) this.grid.setAttribute('aria-busy', 'false');
    if (!this.status) return;
    this.status.hidden = false;
    this.status.innerHTML =
      '<div class="sp-status__title">No promotions available</div>' +
      '<p class="sp-status__msg">' + escapeHtml(message || 'Check back soon.') + '</p>';
  };

  /* ---------- Filter controls (brand modal + category popover + sort) ---------- */
  Engine.prototype.buildFilterControls = function () {
    var self = this;

    // Brand + category facets with live counts (over ALL promos, locked or not).
    this.brandCounts = keyMap();
    this.categoryCounts = keyMap();
    this.promos.forEach(function (entry) {
      var b = entry.promo.brand;
      if (b) self.brandCounts[b] = (self.brandCounts[b] || 0) + 1;
      (entry.promo.tags || []).forEach(function (t) {
        self.categoryCounts[t] = (self.categoryCounts[t] || 0) + 1;
      });
    });

    // Enable the (initially disabled) filter pills + sort.
    var brandBtn = $('#sp-brand-filter', this.root);
    var catBtn = $('#sp-cat-filter', this.root);
    var sortSel = $('#sp-sort', this.root);
    var search = $('#sp-search', this.root);

    if (brandBtn) {
      brandBtn.disabled = false;
      brandBtn.addEventListener('click', function () { self.openBrandModal(); });
    }
    if (catBtn) {
      catBtn.disabled = false;
      catBtn.addEventListener('click', function () { self.openCategoryPopover(catBtn); });
    }
    // Mobile-stuck compact entry — opens the combined Brands+Categories+Sort modal.
    var allBtn = $('#sp-filter-all', this.root);
    if (allBtn) {
      allBtn.disabled = false;
      allBtn.addEventListener('click', function () { self.openAllFilters(); });
    }
    if (sortSel) {
      sortSel.value = this.filters.sort = (this.cfg.defaultSort || 'featured');
      sortSel.addEventListener('change', function () {
        self.filters.sort = sortSel.value;
        self.applyFilters();
      });
    }
    if (search) {
      var t = null;
      search.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          self.filters.search = search.value.trim().toLowerCase();
          self.applyFilters();
        }, 140);
      });
    }
  };

  Engine.prototype.activeBrandList = function () {
    var brands = this.filters.brands;
    return Object.keys(brands).filter(function (k) { return brands[k]; });
  };
  Engine.prototype.activeCategoryList = function () {
    var categories = this.filters.categories;
    return Object.keys(categories).filter(function (k) { return categories[k]; });
  };

  /* ---------- Filter + sort pipeline ---------- */
  Engine.prototype.applyFilters = function () {
    var f = this.filters;
    var brands = this.activeBrandList();
    var cats = this.activeCategoryList();

    var result = this.promos.filter(function (entry) {
      var p = entry.promo;
      if (f.search) {
        var hay = (p.title + ' ' + p.brand + ' ' + (p.tags || []).join(' ')).toLowerCase();
        if (hay.indexOf(f.search) === -1) return false;
      }
      if (brands.length && brands.indexOf(p.brand) === -1) return false;
      if (cats.length) {
        var has = (p.tags || []).some(function (t) { return cats.indexOf(t) !== -1; });
        if (!has) return false;
      }
      return true;
    });

    result.sort(this.sortComparator(f.sort));
    this.filtered = result;
    this.renderGrid();
    this.renderChips();
    this.updateCounts();
    this.updatePillLabels();
  };

  Engine.prototype.sortComparator = function (sort) {
    if (sort === 'a-z') {
      return function (a, b) { return a.promo.brand.localeCompare(b.promo.brand); };
    }
    if (sort === 'newest') {
      return function (a, b) {
        var da = a.promo.dateAdded || '';
        var db = b.promo.dateAdded || '';
        return db.localeCompare(da); // ISO desc = newest first
      };
    }
    // featured: featured flag, then sortWeight desc, then brand A-Z.
    return function (a, b) {
      var fa = a.promo.featured ? 1 : 0;
      var fb = b.promo.featured ? 1 : 0;
      if (fa !== fb) return fb - fa;
      if (a.promo.sortWeight !== b.promo.sortWeight) return b.promo.sortWeight - a.promo.sortWeight;
      return a.promo.brand.localeCompare(b.promo.brand);
    };
  };

  /* ---------- Grid render ---------- */
  // grouping === 'brand': emit each brand as a packed <section.brand-group> block —
  // its .grid-brand-head hairline header + its cards in a nested .brand-cards grid.
  // Each block spans min(cardCount, columns) of the outer grid and flows/wraps
  // left-to-right (CSS grid-auto-flow: row), so short brands pack several-per-row.
  // The nested grid reuses the outer grid's column/gap math, so every card aligns
  // to the same shared vertical tracks regardless of its brand's card count.
  // Indices stay GLOBAL (data-sp-index -> this.filtered) so the card click still
  // opens the right modal. Brand order = first appearance in the sorted filtered
  // list, so it respects the active Sort (A-Z sort => alphabetical groups).
  Engine.prototype.groupedGridHtml = function (entries, cfg) {
    var order = [], groups = keyMap();
    entries.forEach(function (entry, i) {
      var b = entry.promo.brand || 'Other';
      if (!groups[b]) { groups[b] = []; order.push(b); }
      groups[b].push({ entry: entry, i: i });
    });
    var cols = { compact: 5, regular: 4, comfy: 3 }[cfg.density] || 5;
    return order.map(function (brand) {
      var items = groups[brand], n = items.length;
      var span = Math.min(n, cols);
      var head = '<div class="grid-brand-head"><h2>' + escapeHtml(brand) + '</h2>' +
        '<span>' + n + ' ' + (n === 1 ? 'promotion' : 'promotions') + '</span></div>';
      var cards = items.map(function (it) {
        return cardHtml(it.entry.promo, it.entry.resolved, it.entry.access, it.i, cfg, true);
      }).join('');
      return '<section class="brand-group bg-' + span + '">' + head +
        '<div class="brand-cards">' + cards + '</div></section>';
    }).join('');
  };

  Engine.prototype.renderGrid = function () {
    if (!this.grid) return;
    this.hideStatus();
    this.grid.setAttribute('role', (this.cfg && this.cfg.grouping === 'brand') ? 'none' : 'list');
    // Scope the packed-group layout to grouped mode only; flat mode is untouched.
    this.grid.classList.toggle('grouped', !!(this.cfg && this.cfg.grouping === 'brand'));

    if (!this.filtered.length) {
      this.grid.setAttribute('aria-busy', 'false');
      this.grid.innerHTML =
        '<div class="sp-status"><div class="sp-status__title">No promotions found</div>' +
        '<p class="sp-status__msg">Try clearing a filter or searching a different brand.</p>' +
        '<button type="button" class="sp-retry-btn" data-sp-clear-all>Clear filters</button></div>';
      var self0 = this;
      var clr = $('[data-sp-clear-all]', this.grid);
      if (clr) clr.addEventListener('click', function () { self0.clearAllFilters(); });
      return;
    }

    var cfg = this.cfg;
    var html = (cfg.grouping === 'brand')
      ? this.groupedGridHtml(this.filtered, cfg)
      : this.filtered.map(function (entry, i) {
          return cardHtml(entry.promo, entry.resolved, entry.access, i, cfg, false);
        }).join('');
    this.grid.innerHTML = html;
    this.fitBrandTitles();   // grouped mode only (method self-guards) — size titles to fit one line
    this.grid.setAttribute('aria-busy', 'false');
  };

  /* ---------- Brand-title fit-to-width (grouping === 'brand' only) ----------
   * The CSS base-sizes every brand header <h2> at the full 30px editorial size.
   * This pass measures each header after render and shrinks ONLY the titles that
   * would overflow one line, so a short name (BABE, FLOW) keeps 30px even in a
   * narrow mobile group and only genuinely long names (CITY BEAUTY, BIOELEMENTS,
   * L'Oreal Professional) scale down — just enough. Text width is ~linear in
   * font-size, so newSize = 30 * available / natural. The <h2> is min-width:0 +
   * overflow:hidden and the count <span> is flex:none, so scrollWidth (the title's
   * natural width) vs clientWidth (the space actually left beside the count)
   * already accounts for the count. Batched write→read→write to keep the ~30
   * headers to a single reflow instead of one per header. */
  var BRAND_TITLE_MAX = 30;   // px — editorial base size (must match the CSS base)
  var BRAND_TITLE_MIN = 13;   // px — floor; ellipsis is the last resort below this

  Engine.prototype.fitBrandTitles = function () {
    if (!this.grid) return;
    if (!(this.cfg && this.cfg.grouping === 'brand')) return;   // no-op in flat mode
    var heads = $all('.grid-brand-head h2', this.grid);
    if (!heads.length) return;
    // Pass 1 (write): reset every title to the base size so measurement is uniform.
    heads.forEach(function (h) { h.style.fontSize = BRAND_TITLE_MAX + 'px'; });
    // Pass 2 (read): one batched reflow — natural text width vs the box's available width.
    var meas = heads.map(function (h) {
      return { el: h, natural: h.scrollWidth, avail: h.clientWidth };
    });
    // Pass 3 (write): shrink only titles that overflow. The +1px tolerance keeps
    // sub-pixel rounding from nudging a title that already fits below 30px.
    meas.forEach(function (m) {
      if (m.avail > 0 && m.natural > m.avail + 1) {
        var size = Math.floor(BRAND_TITLE_MAX * m.avail / m.natural);
        if (size < BRAND_TITLE_MIN) size = BRAND_TITLE_MIN;
        if (size > BRAND_TITLE_MAX) size = BRAND_TITLE_MAX;
        m.el.style.fontSize = size + 'px';
      }
    });
  };

  // Bind the re-fit triggers ONCE per engine. Group widths change across
  // breakpoints (debounced resize) and the display font (var(--font-display)) may
  // load after first paint and change text metrics (document.fonts.ready). Both are
  // guarded so this is inert in the node:vm test sandbox, where the fake window has
  // no addEventListener and document has no fonts.
  Engine.prototype.setupBrandTitleFit = function () {
    if (this.brandFitBound) return;
    this.brandFitBound = true;
    var self = this;
    if (typeof window !== 'undefined' && window && typeof window.addEventListener === 'function') {
      var timer = null;
      window.addEventListener('resize', function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () { timer = null; self.fitBrandTitles(); }, 100);
      });
    }
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () { self.fitBrandTitles(); });
    }
  };

  Engine.prototype.updateCounts = function () {
    var shown = $('#sp-shown-count', this.root);
    var total = $('#sp-total-count', this.root);
    if (shown) shown.textContent = String(this.filtered.length);
    if (total) total.textContent = String(this.promos.length);
  };

  Engine.prototype.updatePillLabels = function () {
    var brandBtn = $('#sp-brand-filter', this.root);
    var catBtn = $('#sp-cat-filter', this.root);
    var bCount = $('#sp-brand-count', this.root);
    var cCount = $('#sp-cat-count', this.root);
    var brands = this.activeBrandList();
    var cats = this.activeCategoryList();
    if (brandBtn) brandBtn.classList.toggle('active', brands.length > 0);
    if (catBtn) catBtn.classList.toggle('active', cats.length > 0);
    if (bCount) {
      if (brands.length) { bCount.hidden = false; bCount.textContent = String(brands.length); }
      else bCount.hidden = true;
    }
    if (cCount) {
      if (cats.length) { cCount.hidden = false; cCount.textContent = String(cats.length); }
      else cCount.hidden = true;
    }
    // Mobile filter icon: badge the total active brand+category filters.
    var allBtn = $('#sp-filter-all', this.root);
    var allCount = $('#sp-allfilter-count', this.root);
    var active = brands.length + cats.length;
    if (allBtn) allBtn.classList.toggle('active', active > 0);
    if (allCount) {
      if (active) { allCount.hidden = false; allCount.textContent = String(active); }
      else allCount.hidden = true;
    }
  };

  /* ---------- Active filter chips ---------- */
  Engine.prototype.renderChips = function () {
    var wrap = $('#sp-chips', this.root);
    if (!wrap) return;
    var self = this;
    var chips = [];
    if (this.filters.search) {
      chips.push(chipHtml('search', 'Search: ' + this.filters.search));
    }
    this.activeBrandList().forEach(function (b) { chips.push(chipHtml('brand:' + b, b)); });
    this.activeCategoryList().forEach(function (c) { chips.push(chipHtml('cat:' + c, c)); });
    if (chips.length) chips.push('<button type="button" class="filt-clearall" data-sp-clear-all>Clear all</button>');
    wrap.innerHTML = chips.join('');

    $all('[data-sp-chip]', wrap).forEach(function (btn) {
      btn.addEventListener('click', function () { self.removeChip(btn.getAttribute('data-sp-chip')); });
    });
    var clr = $('[data-sp-clear-all]', wrap);
    if (clr) clr.addEventListener('click', function () { self.clearAllFilters(); });
  };

  function chipHtml(key, label) {
    return '<span class="filt-chip">' + escapeHtml(label) +
      '<button type="button" data-sp-chip="' + escapeHtml(key) + '" aria-label="Remove ' + escapeHtml(label) + '">' +
      '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
      '</button></span>';
  }

  Engine.prototype.removeChip = function (key) {
    if (key === 'search') {
      this.filters.search = '';
      var s = $('#sp-search', this.root);
      if (s) s.value = '';
    } else if (key.indexOf('brand:') === 0) {
      delete this.filters.brands[key.slice(6)];
    } else if (key.indexOf('cat:') === 0) {
      delete this.filters.categories[key.slice(4)];
    }
    this.applyFilters();
  };

  Engine.prototype.clearAllFilters = function () {
    this.filters.search = '';
    this.filters.brands = keyMap();
    this.filters.categories = keyMap();
    var s = $('#sp-search', this.root);
    if (s) s.value = '';
    this.applyFilters();
  };

  /* ---------- Brand multi-select modal ---------- */
  Engine.prototype.openBrandModal = function () {
    var self = this;
    var brands = Object.keys(this.brandCounts).sort();
    var selected = keyMap();
    this.activeBrandList().forEach(function (b) { selected[b] = true; });

    var overlay = document.createElement('div');
    overlay.className = 'fm-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Filter by brand');

    function rows(filterText) {
      var ft = (filterText || '').toLowerCase();
      var visible = brands.filter(function (b) { return b.toLowerCase().indexOf(ft) !== -1; });
      if (!visible.length) return '<div class="fm-empty">No brands match.</div>';
      return visible.map(function (b) {
        var count = self.brandCounts[b];
        var on = selected[b] ? ' on' : '';
        var dim = count === 0 ? ' dim' : '';
        return '<button type="button" class="bm-row' + on + dim + '" data-brand="' + escapeHtml(b) + '" aria-pressed="' + (selected[b] ? 'true' : 'false') + '">' +
          '<span class="bm-check">' + (selected[b] ? CHECK_SVG : '') + '</span>' +
          '<span class="bm-name">' + escapeHtml(b) + '</span>' +
          '<span class="bm-count">' + count + '</span></button>';
      }).join('');
    }

    overlay.innerHTML =
      '<div class="fm-panel">' +
      '<div class="fm-head"><div class="fm-title-row"><h2 class="fm-title">Brands</h2>' +
      '<span class="fm-sub">' + brands.length + ' brands</span></div>' +
      '<label class="fm-search"><svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.4"/><path d="M10.5 10.5 L14 14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>' +
      '<input type="text" placeholder="Search brands…" aria-label="Search brands"></label></div>' +
      '<div class="fm-body"><div class="fm-grid" data-fm-grid>' + rows('') + '</div></div>' +
      '<div class="fm-foot"><button type="button" class="fm-clear" data-fm-clear>Clear</button>' +
      '<button type="button" class="fm-apply" data-fm-apply>Apply</button></div></div>';

    // Append inside the section root (not document.body) so the scoped
    // `.shoppable-promotions .fm-overlay/.fm-panel/.bm-row` styles apply — the
    // overlay is position:fixed and the root creates no containing block, so it
    // still covers the viewport. (Appended to body it rendered unstyled at the
    // bottom of the page and the focus trap scrolled there.)
    this.root.appendChild(overlay);
    this.trapModal(overlay, function () { self.closeFloating(overlay); });

    var grid = $('[data-fm-grid]', overlay);
    var searchInput = $('.fm-search input', overlay);

    grid.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('.bm-row') : null;
      if (!row || row.classList.contains('dim')) return;
      var b = row.getAttribute('data-brand');
      if (selected[b]) delete selected[b]; else selected[b] = true;
      setFacetRowState(row, !!selected[b]);
    });
    if (searchInput) searchInput.addEventListener('input', function () {
      grid.innerHTML = rows(searchInput.value);
    });
    $('[data-fm-clear]', overlay).addEventListener('click', function () {
      selected = keyMap();
      $all('.bm-row', grid).forEach(function (r) { setFacetRowState(r, false); });
    });
    $('[data-fm-apply]', overlay).addEventListener('click', function () {
      self.filters.brands = selected;
      self.closeFloating(overlay);
      self.applyFilters();
    });
    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) self.closeFloating(overlay);
    });
  };

  /* ---------- Category popover (anchored) ---------- */
  Engine.prototype.openCategoryPopover = function (anchor) {
    var self = this;
    var existing = anchor.querySelector ? anchor.querySelector('.filt-pop') : null;
    if (anchor.getAttribute('aria-expanded') === 'true' || existing) {
      if (existing && existing._spClose) existing._spClose();
      else {
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        anchor.setAttribute('aria-expanded', 'false');
        if (anchor.focus) anchor.focus();
      }
      return;
    }
    var cats = Object.keys(this.categoryCounts).sort();
    var selected = keyMap();
    this.activeCategoryList().forEach(function (c) { selected[c] = true; });

    var pop = document.createElement('div');
    pop.className = 'filt-pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-modal', 'true');
    pop.setAttribute('aria-label', 'Filter by category');
    // Popover is nested inside its trigger; inner clicks must not re-enter the trigger handler.
    pop.addEventListener('click', function (e) { e.stopPropagation(); });

    function rows() {
      return cats.map(function (c) {
        var on = selected[c] ? ' on' : '';
        return '<button type="button" class="bm-row' + on + '" data-cat="' + escapeHtml(c) + '" aria-pressed="' + (selected[c] ? 'true' : 'false') + '">' +
          '<span class="bm-check">' + (selected[c] ? CHECK_SVG : '') + '</span>' +
          '<span class="bm-name">' + escapeHtml(c) + '</span>' +
          '<span class="bm-count">' + self.categoryCounts[c] + '</span></button>';
      }).join('');
    }

    pop.innerHTML =
      '<div class="fm-head"><div class="fm-title-row"><h2 class="fm-title">Categories</h2></div></div>' +
      '<div class="fm-body"><div class="fm-grid" data-fm-grid>' + rows() + '</div></div>' +
      '<div class="fm-foot"><button type="button" class="fm-clear" data-fm-clear>Clear</button>' +
      '<button type="button" class="fm-apply" data-fm-apply>Apply</button></div>';

    // Anchor the popover inside the pill (which is position:relative).
    anchor.appendChild(pop);
    anchor.setAttribute('aria-expanded', 'true');

    var grid = $('[data-fm-grid]', pop);
    grid.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('.bm-row') : null;
      if (!row) return;
      var c = row.getAttribute('data-cat');
      if (selected[c]) delete selected[c]; else selected[c] = true;
      setFacetRowState(row, !!selected[c]);
    });

    function close() {
      document.removeEventListener('mousedown', onDoc, true);
      if (pop._spKeyHandler) {
        document.removeEventListener('keydown', pop._spKeyHandler, true);
        pop._spKeyHandler = null;
      }
      if (pop.parentNode) pop.parentNode.removeChild(pop);
      anchor.setAttribute('aria-expanded', 'false');
      if (anchor.focus) anchor.focus();
      pop._spLastFocus = null;
    }
    pop._spClose = close;
    function onDoc(e) {
      if (!pop.contains(e.target) && e.target !== anchor && !anchor.contains(e.target)) close();
    }
    this.trapModal(pop, close);
    pop._spLastFocus = anchor;
    // Defer so the opening click doesn't immediately close it.
    setTimeout(function () { document.addEventListener('mousedown', onDoc, true); }, 0);

    $('[data-fm-clear]', pop).addEventListener('click', function () {
      selected = keyMap();
      $all('.bm-row', grid).forEach(function (r) { setFacetRowState(r, false); });
    });
    $('[data-fm-apply]', pop).addEventListener('click', function () {
      self.filters.categories = selected;
      close();
      self.applyFilters();
    });
  };

  // Combined filter modal (Brands + Categories + Sort) — the mobile-stuck filter
  // icon opens this. Reuses the .fm-overlay grouped-facet chrome + .bm-row rows.
  Engine.prototype.openAllFilters = function () {
    var self = this;
    var brands = Object.keys(this.brandCounts).sort();
    var cats = Object.keys(this.categoryCounts).sort();
    var selB = keyMap(); this.activeBrandList().forEach(function (b) { selB[b] = true; });
    var selC = keyMap(); this.activeCategoryList().forEach(function (c) { selC[c] = true; });
    var sortSrc = $('#sp-sort', this.root);
    var sortOpts = sortSrc ? $all('option', sortSrc).map(function (o) { return { value: o.value, name: o.textContent }; }) : [];

    var overlay = document.createElement('div');
    overlay.className = 'fm-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Filter promotions');

    function rowsHtml(names, counts, sel, kind) {
      return names.map(function (n) {
        var c = counts[n] || 0;
        var on = sel[n] ? ' on' : '';
        var dim = c === 0 ? ' dim' : '';
        return '<button type="button" class="bm-row' + on + dim + '" data-kind="' + kind + '" data-name="' + escapeHtml(n) + '" aria-pressed="' + (sel[n] ? 'true' : 'false') + '">' +
          '<span class="bm-check">' + (sel[n] ? CHECK_SVG : '') + '</span>' +
          '<span class="bm-name">' + escapeHtml(n) + '</span>' +
          '<span class="bm-count">' + c + '</span></button>';
      }).join('');
    }

    var groups = '';
    if (sortOpts.length) {
      groups += '<div class="fm-group"><div class="fm-group-h">Sort</div>' +
        '<select class="fm-sort" data-fm-sort>' + sortOpts.map(function (o) {
          return '<option value="' + escapeHtml(o.value) + '"' + (o.value === self.filters.sort ? ' selected' : '') + '>' + escapeHtml(o.name) + '</option>';
        }).join('') + '</select></div>';
    }
    if (brands.length) {
      groups += '<div class="fm-group"><div class="fm-group-h">Brands</div><div class="fm-rows">' + rowsHtml(brands, this.brandCounts, selB, 'brand') + '</div></div>';
    }
    if (cats.length) {
      groups += '<div class="fm-group"><div class="fm-group-h">Categories</div><div class="fm-rows">' + rowsHtml(cats, this.categoryCounts, selC, 'cat') + '</div></div>';
    }

    overlay.innerHTML =
      '<div class="fm-panel">' +
      '<div class="fm-head"><div class="fm-title-row"><h2 class="fm-title">Filter</h2></div></div>' +
      '<div class="fm-body fm-body--groups" data-fm-body>' + groups + '</div>' +
      '<div class="fm-foot"><button type="button" class="fm-clear" data-fm-clear>Clear all</button>' +
      '<button type="button" class="fm-apply" data-fm-apply>Apply</button></div></div>';

    this.root.appendChild(overlay);
    this.trapModal(overlay, function () { self.closeFloating(overlay); });

    var body = $('[data-fm-body]', overlay);
    body.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('.bm-row') : null;
      if (!row || row.classList.contains('dim')) return;
      var sel = row.getAttribute('data-kind') === 'brand' ? selB : selC;
      var name = row.getAttribute('data-name');
      if (sel[name]) delete sel[name]; else sel[name] = true;
      setFacetRowState(row, !!sel[name]);
    });
    $('[data-fm-clear]', overlay).addEventListener('click', function () {
      selB = keyMap(); selC = keyMap();
      $all('.bm-row', overlay).forEach(function (r) {
        setFacetRowState(r, false);
      });
    });
    $('[data-fm-apply]', overlay).addEventListener('click', function () {
      self.filters.brands = selB;
      self.filters.categories = selC;
      var ss = $('[data-fm-sort]', overlay);
      if (ss) { self.filters.sort = ss.value; if (sortSrc) sortSrc.value = ss.value; }
      self.closeFloating(overlay);
      self.applyFilters();
    });
    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) self.closeFloating(overlay);
    });
  };

  /* ---------- Marquee hero population (optional) ---------- */
  Engine.prototype.populateMarquee = function () {
    var track = $('[data-sp-marquee]', this.root);
    if (!track) return;
    var brands = Object.keys(this.brandCounts);
    if (!brands.length) return;
    var items = brands.concat(brands).map(function (b) {
      return '<span class="marquee-item"><span class="marquee-dot"></span>' + escapeHtml(b) + '</span>';
    }).join('');
    track.innerHTML = items;
  };

  /* ----------------------------------------------------------------------
   * 8. Global / grid event delegation
   * -------------------------------------------------------------------- */
  Engine.prototype.bindGlobalEvents = function () {
    if (this._boundGrid) return;
    this._boundGrid = true;
    var self = this;

    // One delegated click handler for the whole card grid.
    this.grid.addEventListener('click', function (e) {
      var t = e.target;
      // Link CTAs (data-sp-stop) navigate on their own -> never open the modal.
      if (t.closest && t.closest('[data-sp-stop]')) return;
      // Locked card actions are real links -> let them navigate.
      if (t.closest && t.closest('.pc-locked-btn')) return;

      var card = t.closest ? t.closest('.pc') : null;
      if (!card || card.classList.contains('pc--locked')) return;
      var idx = parseInt(card.getAttribute('data-sp-index'), 10);
      var entry = self.filtered[idx];
      if (entry) self.openModal(entry, card);
    });

    // Keyboard: Enter/Space on a focused card opens the modal.
    this.grid.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest ? e.target.closest('.pc') : null;
      if (!card || card.classList.contains('pc--locked')) return;
      if (e.target.closest('[data-sp-stop]') || e.target.closest('.pc-locked-btn')) return;
      e.preventDefault();
      var idx = parseInt(card.getAttribute('data-sp-index'), 10);
      var entry = self.filtered[idx];
      if (entry) self.openModal(entry, card);
    });
  };

  /* ----------------------------------------------------------------------
   * 9. Cloudflare challenge contract (spec §7)
   *     Product modal Add buttons render disabled while a challenge is active;
   *     re-enable the sticky-bar button on cart:challenge:cleared.
   *     The injected collection grid's .cg-* buttons are handled by collection-atc.js.
   * -------------------------------------------------------------------- */
  Engine.prototype.bindChallenge = function () {
    if (this.challengeBound) return;
    this.challengeBound = true;
    var self = this;
    document.addEventListener('cart:challenge:cleared', function () {
      $all('.rs-bar [data-sp-add]', self.root).forEach(function (b) { b.disabled = false; });
    });
  };

  /* ----------------------------------------------------------------------
   * 10. Detail modal (spec §5 collection / §6 product)
   * -------------------------------------------------------------------- */
  Engine.prototype.openModal = function (entry, triggerEl) {
    var self = this;
    var promo = entry.promo;
    var resolved = entry.resolved;

    // Link promos have NO modal buy-surface (openModal only loads collection /
    // product sources) — navigate to the safe fallback instead, matching the CTA
    // anchor. Guarded at the top so BOTH the click and keyboard paths are covered.
    // A neutralized href ('#') means there is nowhere safe to go -> do nothing.
    if (resolved.type === 'link') {
      var linkHref = safeHref(resolved.fallbackLink);
      if (linkHref && linkHref !== '#' && window.location) window.location.href = linkHref;
      return;
    }

    this.lastFocus = triggerEl || document.activeElement;

    var rsm = document.createElement('div');
    rsm.className = 'rsm';
    rsm.innerHTML = (resolved.type === 'product')
      ? this.productModalHtml(promo)
      : this.collectionModalHtml(promo);

    this.modalRoot.innerHTML = '';
    this.modalRoot.appendChild(rsm);
    document.body.style.overflow = 'hidden';

    // Paint the product stage's sage/rouge field immediately (before the fetch).
    if (resolved.type === 'product') {
      var st = $('.rs-stage', rsm);
      this.paintStage(rsm, !!(st && st.getAttribute('data-sp-parity') === '1'));
    }

    var overlay = $('.rs-overlay', rsm);
    var dialog = $('.rs-modal', rsm);
    var closeBtn = $('.rs-close', rsm);

    function close() { self.closeModal(rsm); }
    if (closeBtn) closeBtn.addEventListener('click', close);
    $all('[data-sp-close]', rsm).forEach(function (b) { b.addEventListener('click', close); });
    if (overlay) overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) close();
    });

    // Focus trap + Esc.
    this._modalKeyHandler = function (e) {
      if (e.key === 'Escape') {
        if (self.root && self.root.querySelector && self.root.querySelector('.fm-overlay')) return;
        e.stopPropagation(); close(); return;
      }
      if (e.key === 'Tab' && dialog) {
        var f = $all('button, select, input, a[href], [tabindex]:not([tabindex="-1"])', dialog)
          .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', this._modalKeyHandler, true);
    if (closeBtn) closeBtn.focus();

    // Load the gated buy surface.
    if (resolved.type === 'collection') {
      this.loadCollectionGrid(resolved, $('#sp-modal-grid', rsm), promo);
    } else if (resolved.type === 'product') {
      this.loadProductBuy(resolved, rsm, promo);
    }
  };

  Engine.prototype.closeModal = function (rsm) {
    if (this._modalKeyHandler) {
      document.removeEventListener('keydown', this._modalKeyHandler, true);
      this._modalKeyHandler = null;
    }
    document.body.style.overflow = '';
    if (rsm && rsm.parentNode) rsm.parentNode.removeChild(rsm);
    else this.modalRoot.innerHTML = '';
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    this.lastFocus = null;
  };

  /* ---------- Modal markup ---------- */
  Engine.prototype.modalMetaHtml = function (promo) {
    var bits = [];
    if (promo.dates && promo.dates.display) {
      bits.push('<span class="m">' + SVG.clock + '<b>' + escapeHtml(promo.dates.display) + '</b></span>');
    }
    var inStock = !promo.status || promo.status.inStock !== false;
    bits.push('<span class="m"><span class="dot' + (inStock ? '' : ' out') + '"></span>' +
      (inStock ? 'In stock' : 'Out of stock') + '</span>');
    return '<div class="rs-meta">' + bits.join('') + '</div>';
  };

  Engine.prototype.framingLineHtml = function (promo, klass) {
    var txt = framingText(promo);
    if (!txt) return '';
    return '<div class="' + klass + '"><span class="val sm">' + escapeHtml(txt) + '</span></div>';
  };

  Engine.prototype.includedHtml = function (promo) {
    if (!promo.included || !promo.included.length) return '';
    var rows = promo.included.map(function (it) {
      var isFree = it.note && /free/i.test(it.note);
      var note = it.note
        ? (isFree
          ? '<span class="rs-badge-free">FREE</span>'
          : '<span class="rs-note-tag">' + escapeHtml(it.note) + '</span>')
        : '';
      // The feed carries no per-item image (only sku/name/qty), so the thumb is
      // a placeholder: a gift for FREE items, a product glyph otherwise.
      var thumb = isFree
        ? '<div class="rs-item-thumb free">' + SVG.gift + '</div>'
        : '<div class="rs-item-thumb">' + SVG.bottle + '</div>';
      return '<div class="rs-item">' + thumb +
        '<div class="rs-item-body"><span class="rs-item-name">' + escapeHtml(it.label) + '</span></div>' +
        note + '</div>';
    }).join('');
    var explicitQty = promo.included.reduce(function (sum, it) { return sum + includedNoteQty(it.note); }, 0);
    var itemCount = explicitQty > 0 ? explicitQty : promo.included.length; // fall back to rows only when no ×N present
    var countLabel = itemCount + (itemCount === 1 ? ' item' : ' items');
    return '<div class="rs-sec"><div class="rs-sec-h"><span class="bar"></span>What\'s included' +
      '<span class="count">' + countLabel + '</span></div>' + rows + '</div>';
  };

  Engine.prototype.termsHtml = function (promo) {
    if (!promo.terms) return '';
    return '<div class="rs-sec"><div class="rs-sec-h"><span class="bar"></span>Terms</div>' +
      '<ul class="rs-terms"><li>' + escapeHtml(promo.terms) + '</li></ul></div>';
  };

  Engine.prototype.gridStatusHtml = function (title, msg) {
    return '<div class="sp-status"><div class="sp-spinner" aria-hidden="true"></div>' +
      '<div class="sp-status__title">' + escapeHtml(title) + '</div>' +
      (msg ? '<p class="sp-status__msg">' + escapeHtml(msg) + '</p>' : '') + '</div>';
  };

  Engine.prototype.collectionModalHtml = function (promo) {
    var img = promo.image
      ? '<img class="rs-chero-img" src="' + escapeHtml(safeHref(promo.image)) + '" alt="' + escapeHtml(promo.title) + '">'
      : '';
    var chip = (promo.status && promo.status.limitedTime) ? 'Limited edition' : 'Pro exclusive';
    var sub = promo.subtitle ? '<span class="sub">' + escapeHtml(promo.subtitle) + '</span>' : '';

    return '<div class="rs-overlay">' +
      '<div class="rs-modal rs-modal--collection" role="dialog" aria-modal="true" aria-labelledby="rsTitle">' +
      '<button class="rs-close" type="button" aria-label="Close">' + SVG.close + '</button>' +
      '<div class="rs-cscroll">' +
      '<div class="rs-chead"><div class="rs-chero">' + img +
      '<span class="rs-chip"><i></i>' + chip + '</span></div>' +
      '<div class="rs-cintro">' +
      '<div class="rs-brandline"><span class="bar"></span><span class="name">' + escapeHtml(promo.brand) + '</span></div>' +
      '<h1 id="rsTitle" class="' + titleClass(promo.title) + '">' + escapeHtml(promo.title) + sub + '</h1>' +
      this.modalMetaHtml(promo) +
      this.framingLineHtml(promo, 'rs-cprice') +
      (promo.description ? '<p class="rs-desc">' + richText(promo.description) + '</p>' : '') +
      '</div></div>' +
      '<div class="rs-cbody"><div class="rs-sec">' +
      '<div class="rs-sec-h"><span class="bar"></span>In this collection<span class="count" data-sp-grid-count></span>' +
      '<button type="button" class="filt-pill rs-collfilter" data-sp-collfilter hidden aria-haspopup="dialog">' + SVG.filter +
      '<span class="filt-pill-label">Filter</span>' +
      '<span class="filt-pill-count" data-sp-collfilter-count hidden></span></button></div>' +
      '<div id="sp-modal-grid" class="sp-modal-grid" data-sp-kind="collection">' +
      this.gridStatusHtml('Loading products…', '') + '</div>' +
      '</div>' +
      this.termsHtml(promo) +
      this.footCloseHtml() +
      '</div></div></div>';
  };

  // A right-aligned "Close" button at the end of a modal's scroll content
  // (bottom-right), in addition to the top-right X. Bound via [data-sp-close].
  Engine.prototype.footCloseHtml = function () {
    return '<button type="button" class="rs-foot-close" data-sp-close aria-label="Close">' +
      SVG.close + '<span class="rs-foot-close-label">Close</span></button>';
  };

  // Three product-modal views share this shell. The receipt, the buy-body (multi-
  // variant list) and the sticky foot (single-variant bar) are filled by
  // loadProductBuy() after /products/{handle}.js resolves.
  Engine.prototype.productModalHtml = function (promo) {
    var chip = (promo.status && promo.status.limitedTime) ? 'Limited edition' : 'Pro exclusive';
    var sub = promo.subtitle ? '<span class="sub">' + escapeHtml(promo.subtitle) + '</span>' : '';
    var cardImg = promo.image
      ? '<img src="' + escapeHtml(safeHref(promo.image)) + '" alt="' + escapeHtml(promo.title) + '">'
      : '';
    var parity = spHash(promo.id) % 2; // 0 = sage, 1 = rouge (painted after insert)

    return '<div class="rs-overlay">' +
      '<div class="rs-modal rs-modal--product" role="dialog" aria-modal="true" aria-labelledby="rsTitle">' +
      '<button class="rs-close" type="button" aria-label="Close">' + SVG.close + '</button>' +
      // Mobile banner
      '<div class="rs-banner"><div class="rs-banner-img">' + cardImg + '</div>' +
      '<span class="rs-chip"><i></i>' + chip + '</span></div>' +
      // Left stage — sage/rouge field + wave pattern behind the product card.
      '<div class="rs-stage" data-sp-parity="' + parity + '">' +
      '<div class="rs-field" data-sp-field></div>' +
      '<div class="rs-pattern" data-sp-pattern></div>' +
      '<span class="rs-chip"><i></i>' + chip + '</span>' +
      '<div class="rs-cardwrap"><div class="rs-card"><div class="rs-cardinner" data-sp-stage-main>' + cardImg + '</div></div></div>' +
      '<div class="rs-thumbs" data-sp-thumbs role="tablist" aria-label="Product views" hidden></div>' +
      '</div>' +
      // Right column — details + buy surface.
      '<div class="rs-details">' +
      '<div class="rs-scroll">' +
      '<div class="rs-brandline"><span class="bar"></span><span class="name">' + escapeHtml(promo.brand) + '</span></div>' +
      '<h1 id="rsTitle" class="' + titleClass(promo.title) + '">' + escapeHtml(promo.title) + sub + '</h1>' +
      this.modalMetaHtml(promo) +
      '<div data-sp-receipt></div>' +
      (promo.description ? '<p class="rs-desc">' + richText(promo.description) + '</p>' : '') +
      '<div data-sp-buybody>' + this.gridStatusHtml('Loading…', '') + '</div>' +
      this.includedHtml(promo) +
      this.termsHtml(promo) +
      this.footCloseHtml() +
      '</div>' +
      '<div class="rs-foot" data-sp-foot></div>' +
      '</div></div></div>';
  };

  /* ----------------------------------------------------------------------
   * 11. Section-Rendering grid fetch + reinit handshake (spec §5)
   * -------------------------------------------------------------------- */

  // Fetch with retry backoff. Resolves with response text, rejects after the
  // backoff is exhausted.
  function fetchWithRetry(url, attempt) {
    attempt = attempt || 0;
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (res.status === 404) { var e = new Error('404'); e.notFound = true; throw e; }
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    }).catch(function (err) {
      if (err && err.notFound) throw err;
      if (attempt >= RETRY_BACKOFF.length) throw err;
      return new Promise(function (resolve) {
        setTimeout(resolve, RETRY_BACKOFF[attempt]);
      }).then(function () { return fetchWithRetry(url, attempt + 1); });
    });
  }

  function parseGrid(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var grid = doc.querySelector('.sp-collection-grid, .sp-product-grid, [id^="sp-"][class*="grid"]');
    var metaNode = doc.querySelector('[data-sp-grid-meta]');
    var meta = null;
    if (metaNode) { try { meta = JSON.parse(metaNode.textContent); } catch (e) { meta = null; } }
    var filtersNode = doc.querySelector('[data-sp-filters]');
    var filters = null;
    if (filtersNode) { try { filters = JSON.parse(filtersNode.textContent); } catch (e) { filters = null; } }
    return { grid: grid, meta: meta, filters: filters };
  }

  // `query` (optional) is a native Shopify filter/sort querystring
  // (`filter.v.*=…&filter.p.price.gte=…&sort_by=…`); Shopify auto-filters
  // collection.products when the section is fetched with it.
  Engine.prototype.loadCollectionGrid = function (resolved, gridEl, promo, query) {
    if (!gridEl) return;
    var self = this;
    var cacheBust = Date.now();
    var sectionId = (this.cfg.endpoints && this.cfg.endpoints.collection) || 'shoppable-promo-collection';
    var base = '/collections/' + encodeURIComponent(resolved.handle) +
      '?section_id=' + sectionId + '&_spv=' + cacheBust;
    var q = query ? ('&' + query) : '';

    fetchWithRetry(base + q + '&page=1', 0).then(function (html) {
      var first = parseGrid(html);
      // Record the filter state (facets/counts/active) regardless of result size
      // so the pill + popup stay usable even on an empty filtered result.
      self.setCollectionFilters(resolved, gridEl, promo, first.filters, query || '');

      var totalPages = (first.meta && first.meta.total_pages) || 1;
      var page1Count = first.grid ? first.grid.children.length : 0;

      // A product can be FULLY region-gated out of page 1 (hc-product-grid-item
      // drops the whole card), so an empty page 1 does NOT mean the collection is
      // empty — pages 2..N may still hold visible products. Only declare empty
      // after consulting total_pages (and, when >1, fetching the rest below).
      if (!page1Count && totalPages <= 1) {
        self.gridFallback(gridEl, resolved, query
          ? 'No products match these filters.'
          : 'This collection is empty right now.');
        return;
      }

      // Inject page 1 (may be an empty container; later pages append into it).
      gridEl.innerHTML = '';
      var target;
      if (first.grid) { gridEl.appendChild(first.grid); target = first.grid; }
      else { target = gridEl; }
      if (page1Count) self.reinitQuickAdd(gridEl);

      // Fetch pages 2..N in parallel and append (same filter query).
      if (totalPages > 1) {
        var jobs = [];
        for (var p = 2; p <= totalPages; p++) {
          jobs.push(fetchWithRetry(base + q + '&page=' + p, 0).then(function (h) {
            return parseGrid(h).grid;
          }).catch(function () { return null; }));
        }
        Promise.all(jobs).then(function (grids) {
          grids.forEach(function (g) {
            if (!g) return;
            while (g.firstElementChild) target.appendChild(g.firstElementChild);
          });
          // Only NOW is the empty verdict safe: count ACTUALLY-RENDERED cards
          // (region-gated products are dropped from the section HTML), never
          // paginate.items / meta.total_items — which count gated-out products.
          var rendered = target.children ? target.children.length : 0;
          if (!rendered) {
            self.gridFallback(gridEl, resolved, query
              ? 'No products match these filters.'
              : 'This collection is empty right now.');
            return;
          }
          self.fillGridCount(gridEl, rendered);
          self.reinitQuickAdd(gridEl);
        });
      } else {
        // Single page with visible cards: count rendered cards, not meta totals.
        self.fillGridCount(gridEl, page1Count);
      }
    }).catch(function (err) {
      self.gridFallback(gridEl, resolved, err && err.notFound
        ? 'This collection is no longer available.'
        : 'We couldn’t load these products.');
    });
  };

  // Stash the current collection-filter state + reveal/label the header Filter pill.
  Engine.prototype.setCollectionFilters = function (resolved, gridEl, promo, filters, query) {
    this._coll = { resolved: resolved, gridEl: gridEl, promo: promo, filters: filters, query: query || '' };
    var modal = gridEl && gridEl.closest ? gridEl.closest('.rs-modal') : null;
    var pill = modal ? $('[data-sp-collfilter]', modal) : null;
    if (!pill) return;
    var hasFilters = !!(filters && filters.filters && filters.filters.length);
    pill.hidden = !hasFilters;
    if (!hasFilters) return;
    var active = filters.activeCount || 0;
    var countEl = $('[data-sp-collfilter-count]', pill);
    if (countEl) {
      countEl.textContent = active;
      countEl.hidden = active === 0;
    }
    pill.classList.toggle('active', active > 0);
    if (!pill._spBound) {
      pill._spBound = true;
      var self = this;
      pill.addEventListener('click', function () { self.openCollectionFilter(); });
    }
  };

  // Filter popup (reuses the .fm-overlay brand-modal chrome). Renders the
  // collection's native facets; Apply re-fetches the grid with filter params.
  Engine.prototype.openCollectionFilter = function () {
    var self = this;
    var data = this._coll && this._coll.filters;
    if (!data || !data.filters || !data.filters.length) return;

    // Local selection seeded from the current active state.
    var selected = keyMap(); // paramName -> { value: true }
    var price = { gte: '', lte: '' };
    data.filters.forEach(function (f) {
      if (f.type === 'price_range') {
        price.gteName = f.gteName; price.lteName = f.lteName;
        price.min = f.rangeMin; price.max = f.rangeMax; price.prefix = f.prefix || '$';
        if (f.activeMin != null) price.gte = f.activeMin;
        if (f.activeMax != null) price.lte = f.activeMax;
      } else {
        (f.values || []).forEach(function (v) {
          if (v.active) { (selected[v.paramName] = selected[v.paramName] || keyMap())[v.value] = true; }
        });
      }
    });

    var overlay = document.createElement('div');
    // `--coll` bumps the z-index above the collection modal (.rs-overlay z:1000)
    // so this popup, opened from inside that modal, stacks on top of it.
    overlay.className = 'fm-overlay fm-overlay--coll';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Filter products');

    var groups = '';
    if (data.sortOptions && data.sortOptions.length) {
      groups += '<div class="fm-group"><div class="fm-group-h">Sort</div>' +
        '<select class="fm-sort" data-fm-sort>' + data.sortOptions.map(function (o) {
          return '<option value="' + escapeHtml(o.value) + '"' + (o.value === data.sortBy ? ' selected' : '') + '>' + escapeHtml(o.name) + '</option>';
        }).join('') + '</select></div>';
    }
    data.filters.forEach(function (f) {
      groups += '<div class="fm-group"><div class="fm-group-h">' + escapeHtml(f.label) + '</div>';
      if (f.type === 'price_range') {
        groups += '<div class="fm-price">' +
          '<span class="fm-price-pre">' + escapeHtml(price.prefix) + '</span>' +
          '<input type="number" class="fm-price-in" data-fm-price="gte" min="0" inputmode="decimal" placeholder="Min" value="' + escapeHtml(String(price.gte)) + '">' +
          '<span class="fm-price-dash">–</span>' +
          '<input type="number" class="fm-price-in" data-fm-price="lte" min="0" inputmode="decimal" placeholder="Max" value="' + escapeHtml(String(price.lte)) + '"></div>';
      } else {
        groups += '<div class="fm-rows">' + (f.values || []).map(function (v) {
          var on = (selected[v.paramName] && selected[v.paramName][v.value]) ? ' on' : '';
          var dim = (v.count === 0) ? ' dim' : '';
          return '<button type="button" class="bm-row' + on + dim + '" data-param="' + escapeHtml(v.paramName) + '" data-value="' + escapeHtml(v.value) + '" aria-pressed="' + (on ? 'true' : 'false') + '">' +
            '<span class="bm-check">' + (on ? CHECK_SVG : '') + '</span>' +
            '<span class="bm-name">' + escapeHtml(v.label) + '</span>' +
            '<span class="bm-count">' + (v.count || 0) + '</span></button>';
        }).join('') + '</div>';
      }
      groups += '</div>';
    });

    overlay.innerHTML =
      '<div class="fm-panel">' +
      '<div class="fm-head"><div class="fm-title-row"><h2 class="fm-title">Filter</h2></div></div>' +
      '<div class="fm-body fm-body--groups" data-fm-body>' + groups + '</div>' +
      '<div class="fm-foot"><button type="button" class="fm-clear" data-fm-clear>Clear all</button>' +
      '<button type="button" class="fm-apply" data-fm-apply>Apply</button></div></div>';

    this.root.appendChild(overlay);
    this.trapModal(overlay, function () { self.closeFloating(overlay); });

    var body = $('[data-fm-body]', overlay);
    body.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('.bm-row') : null;
      if (!row || row.classList.contains('dim')) return;
      var pn = row.getAttribute('data-param'), val = row.getAttribute('data-value');
      selected[pn] = selected[pn] || keyMap();
      if (selected[pn][val]) delete selected[pn][val]; else selected[pn][val] = true;
      setFacetRowState(row, !!selected[pn][val]);
    });
    $('[data-fm-clear]', overlay).addEventListener('click', function () {
      self.closeFloating(overlay);
      self.applyCollectionFilter('');
    });
    $('[data-fm-apply]', overlay).addEventListener('click', function () {
      var parts = [];
      Object.keys(selected).forEach(function (pn) {
        Object.keys(selected[pn]).forEach(function (val) {
          parts.push(encodeURIComponent(pn) + '=' + encodeURIComponent(val));
        });
      });
      var sortSel = $('[data-fm-sort]', overlay);
      if (sortSel && sortSel.value) parts.push('sort_by=' + encodeURIComponent(sortSel.value));
      var gteIn = $('[data-fm-price="gte"]', overlay), lteIn = $('[data-fm-price="lte"]', overlay);
      if (price.gteName && gteIn && gteIn.value !== '') parts.push(encodeURIComponent(price.gteName) + '=' + encodeURIComponent(gteIn.value));
      if (price.lteName && lteIn && lteIn.value !== '') parts.push(encodeURIComponent(price.lteName) + '=' + encodeURIComponent(lteIn.value));
      self.closeFloating(overlay);
      self.applyCollectionFilter(parts.join('&'));
    });
    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) self.closeFloating(overlay);
    });
  };

  // Re-fetch the collection grid with the chosen native filter/sort querystring.
  Engine.prototype.applyCollectionFilter = function (query) {
    if (!this._coll || !this._coll.gridEl) return;
    var c = this._coll;
    c.gridEl.innerHTML = this.gridStatusHtml('Loading products…', '');
    this.loadCollectionGrid(c.resolved, c.gridEl, c.promo, query);
  };

  /* ----------------------------------------------------------------------
   * 12b. Product buy surface — three views (collection handled above)
   *   - single-variant  -> offer receipt + sticky .rs-bar (qty + Add)
   *   - multi-variant   -> variant list (each a cg-qty ± stepper), no bar
   * Pricing is LIVE Shopify (variant.price / compare_at_price) per the modal
   * decision; the collection view keeps server-side Section Rendering.
   * -------------------------------------------------------------------- */

  // Live presentment-money formatter (no native B2B catalog price — accepted
  // for IB's tag-based gating). Falls back to $X.XX.
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents); } catch (e) {}
    }
    return '$' + (cents / 100).toFixed(2);
  }

  Engine.prototype.loadProductBuy = function (resolved, rsm, promo) {
    var self = this;
    var buyBody = $('[data-sp-buybody]', rsm);
    var receiptMount = $('[data-sp-receipt]', rsm);
    var foot = $('[data-sp-foot]', rsm);
    function removeFoot() { if (foot && foot.parentNode) foot.parentNode.removeChild(foot); }
    function denied(msg) {
      removeFoot();
      if (buyBody) buyBody.innerHTML = '<div class="rs-locked">' + SVG.lock +
        '<p>' + escapeHtml(msg || 'Log in to view pricing.') + '</p>' +
        '<a class="rs-add rs-add--secondary" href="/account/login">Log in</a></div>';
    }
    function fallback() {
      removeFoot();
      var href = escapeHtml(safeHref(resolved.fallbackLink || '#'));
      if (buyBody) buyBody.innerHTML = '<div class="sp-status">' +
        '<div class="sp-status__title">We couldn’t load this product.</div>' +
        '<a class="sp-retry-btn" href="' + href + '">View on the site</a></div>';
    }

    fetch('/products/' + encodeURIComponent(resolved.handle) + '.js', { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('product ' + res.status); return res.json(); })
      .then(function (product) {
        self.wireThumbs(rsm, product, promo);
        var isLoggedIn = !!(self.cfg.customer && self.cfg.customer.isLoggedIn);
        var access = evaluateProduct(product.tags || [], self.customerState, isLoggedIn,
          self.cfg && self.cfg.pricingRestrictionsEnabled);
        if (access && (access.canAddToCart === false || access.canViewPrice === false)) {
          denied(access.message || 'Log in or contact us for pricing.');
          return;
        }
        var variants = product.variants || [];
        if (!variants.length) { fallback(); return; }

        if (variants.length === 1) {
          // VIEW 2 — single variant -> receipt + sticky bar.
          var v = variants[0];
          if (receiptMount) receiptMount.innerHTML = self.receiptHtml(promo, v, false);
          if (buyBody) buyBody.innerHTML = '';
          self.renderStickyBar(foot, v, promo);
        } else {
          // VIEW 3 — multi variant -> variant list, no sticky bar.
          removeFoot();
          var cheapest = variants.reduce(function (a, b) { return (b.price < a.price) ? b : a; }, variants[0]);
          if (receiptMount) receiptMount.innerHTML = self.receiptHtml(promo, cheapest, true);
          if (buyBody) buyBody.innerHTML = self.variantListHtml(product, promo);
          // Engine owns these external steppers + commit (addSilently w/ _promo_id);
          // CollectionATC.rebind refreshes the shared layer without double-binding them.
          self.bindVariantList(buyBody, promo);
          self.reinitQuickAdd(buyBody);
        }
      })
      .catch(fallback);
  };

  // Offer receipt from LIVE price/compare-at. `starting` => "Starting at" label.
  Engine.prototype.receiptHtml = function (promo, variant, starting) {
    if (!variant) return '';
    var price = variant.price || 0;
    var cmp = variant.compare_at_price || 0;
    var hasCompare = cmp > price;
    var label = starting ? 'Starting at'
      : ((promo.included && promo.included.length) ? 'Your bundle price' : 'Your price');
    var pct = hasCompare ? (Math.round((cmp - price) / cmp * 100) + '% OFF') : (badgeText(promo) || '');
    var prices = '<span class="rs-price">' + escapeHtml(formatMoney(price)) + '</span>' +
      (hasCompare ? '<span class="rs-was">' + escapeHtml(formatMoney(cmp)) + '</span>' : '');
    var save = hasCompare
      ? '<div class="rs-receipt-aside"><div class="lbl">You save</div><div class="rs-save">' +
        escapeHtml(formatMoney(cmp - price)) + '</div></div>'
      : '';
    var endLabel = '';
    if (promo.dates && promo.dates.end) {
      var el = ymdLabel(promo.dates.end);
      if (el) endLabel = el + ', ' + String(promo.dates.end).slice(0, 4);
    }
    var ends = endLabel
      ? 'Offer ends <b>' + escapeHtml(endLabel) + '</b>'
      : 'While supplies last';
    return '<div class="rs-receipt" aria-label="Pricing"><div class="rs-receipt-main">' +
      '<div class="rs-receipt-lead"><div class="rs-receipt-top"><span class="lbl">' + label + '</span>' +
      (pct ? '<span class="rs-pct">' + escapeHtml(pct) + '</span>' : '') + '</div>' +
      '<div class="rs-receipt-prices">' + prices + '</div></div>' + save + '</div>' +
      '<div class="rs-receipt-foot">' + SVG.clock + '<span>' + ends + '</span></div></div>';
  };

  // Paint the stage field + wave pattern (sage when rouge=false, rouge otherwise).
  Engine.prototype.paintStage = function (scope, rouge) {
    var f = $('[data-sp-field]', scope), p = $('[data-sp-pattern]', scope);
    if (f) f.style.background = rouge ? 'var(--ib-rouge)' : 'var(--ib-sage)';
    if (p) {
      p.style.backgroundImage = waveUrl(rouge);
      p.style.backgroundRepeat = 'repeat';
      p.style.backgroundSize = '90px 34px';
    }
  };

  // Build the left-stage thumbnail strip from the product's media gallery.
  Engine.prototype.wireThumbs = function (rsm, product, promo) {
    var self = this;
    var thumbsEl = $('[data-sp-thumbs]', rsm);
    var mainEl = $('[data-sp-stage-main]', rsm);
    if (!thumbsEl || !mainEl) return;
    var stage = $('.rs-stage', rsm);
    var base = (stage && stage.getAttribute('data-sp-parity') === '1') ? 1 : 0;
    var images = (product.media || [])
      .map(function (m) { return m && (m.preview_image ? m.preview_image.src : m.src); })
      .filter(Boolean);
    if (!images.length) images = (product.images || []).filter(Boolean);
    if (images.length < 2) return; // single image -> keep the feed banner, no strip
    var alt = escapeHtml(product.title || promo.title);
    mainEl.innerHTML = '<img src="' + escapeHtml(safeHref(images[0])) + '" alt="' + alt + '">';
    thumbsEl.hidden = false;
    thumbsEl.innerHTML = images.slice(0, 5).map(function (src, i) {
      var safe = escapeHtml(safeHref(src));
      return '<button class="rs-thumb" type="button" role="tab" aria-selected="' + (i === 0 ? 'true' : 'false') +
        '" data-sp-img="' + safe + '"><span class="rs-cardinner"><img src="' + safe + '" alt=""></span></button>';
    }).join('');
    thumbsEl.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.rs-thumb') : null;
      if (!btn) return;
      var items = $all('.rs-thumb', thumbsEl);
      var idx = items.indexOf(btn);
      items.forEach(function (t) { t.setAttribute('aria-selected', 'false'); });
      btn.setAttribute('aria-selected', 'true');
      // Index the in-closure images[] rather than reading data-sp-img back out of
      // the DOM: getAttribute returns the parser-DECODED value, so a src with a
      // stray double-quote would break out of this innerHTML. escapeHtml(safeHref)
      // re-sanitizes here exactly as the thumb markup did.
      mainEl.innerHTML = '<img src="' + escapeHtml(safeHref(images[idx])) + '" alt="' + alt + '">';
      // Alternate the field colour per selected view (mock-up behaviour).
      self.paintStage(rsm, (base + idx) % 2 === 1);
    });
  };

  // Single-variant sticky bottom bar: YOU PAY + qty stepper + Add to cart.
  Engine.prototype.renderStickyBar = function (foot, variant, promo) {
    if (!foot) return;
    if (variant.available === false) {
      foot.innerHTML = '<div class="rs-bar"><span class="cg-sold-out">Sold out</span></div>';
      return;
    }
    var cart = window.theme && window.theme.cart;
    var challenged = !!(cart && cart.isChallengeActive && cart.isChallengeActive());
    foot.innerHTML = '<div class="rs-bar" data-sp-variant="' + variant.id + '" data-sp-price="' + (variant.price || 0) + '">' +
      '<div class="pay"><span class="l">You pay</span><span class="v" data-sp-pay>' + escapeHtml(formatMoney(variant.price)) + '</span></div>' +
      '<div class="rs-qty" role="group" aria-label="Quantity">' +
      '<button type="button" data-sp-dec aria-label="Decrease quantity">' + SVG.minus + '</button>' +
      '<span class="n" data-sp-qtyval aria-live="polite">1</span>' +
      '<button type="button" data-sp-inc aria-label="Increase quantity">' + SVG.inc + '</button></div>' +
      '<button type="button" class="rs-add" data-sp-add' + (challenged ? ' disabled' : '') + '>Add to cart' + SVG.arrow + '</button>' +
      '</div>';
    this.bindStickyBar(foot, promo);
  };

  Engine.prototype.bindStickyBar = function (foot, promo) {
    var bar = $('.rs-bar', foot);
    if (!bar) return;
    var qtyEl = $('[data-sp-qtyval]', bar);
    var payEl = $('[data-sp-pay]', bar);
    var price = parseInt(bar.getAttribute('data-sp-price'), 10) || 0;
    var variantId = parseInt(bar.getAttribute('data-sp-variant'), 10);
    function qty() { return Math.max(1, parseInt(qtyEl.textContent, 10) || 1); }
    function paint() { if (payEl) payEl.textContent = formatMoney(price * qty()); }
    var dec = $('[data-sp-dec]', bar), inc = $('[data-sp-inc]', bar), add = $('[data-sp-add]', bar);
    if (dec) dec.addEventListener('click', function () { qtyEl.textContent = Math.max(1, qty() - 1); paint(); });
    if (inc) inc.addEventListener('click', function () { qtyEl.textContent = qty() + 1; paint(); });
    if (add) add.addEventListener('click', function () {
      var cart = window.theme && window.theme.cart;
      if (!cart) return;
      if (cart.isChallengeActive && cart.isChallengeActive()) return; // challenge contract
      add.disabled = true; add.innerHTML = 'Adding…';
      cart.addSilently([{ id: variantId, quantity: qty(), properties: { _promo_id: promo.id } }],
        { source: 'shoppable-promo-add' })
        .then(function () {
          add.classList.add('added'); add.innerHTML = 'Added to cart' + SVG.check;
          setTimeout(function () {
            add.disabled = false; add.classList.remove('added'); add.innerHTML = 'Add to cart' + SVG.arrow;
          }, 1600);
        })
        .catch(function (error) {
          add.disabled = false; add.innerHTML = 'Add to cart' + SVG.arrow;
          PromoVariantCart.showAddFailureToast(error);
        });
    });
  };

  /* ----------------------------------------------------------------------
   * PromoVariantCart controller
   * -------------------------------------------------------------------- */

  var PromoVariantCart = (function () {
    var ADD_FAILURE_GENERIC = 'Unable to add item to cart. Please try again.';
    var variantCartSyncBound = false;
    var variantScopes = [];

    function describeAddError(error) {
      if (window.theme && window.theme.cart && window.theme.cart.describeAddError) {
        return window.theme.cart.describeAddError(error);
      }
      return (error && error.userMessage) || ADD_FAILURE_GENERIC;
    }

    function addErrorText(value) {
      return describeAddError({ status: 422, description: value, userMessage: ADD_FAILURE_GENERIC });
    }

    function addErrorDescription(text) {
      return describeAddError({ status: 422, responseText: text, userMessage: ADD_FAILURE_GENERIC });
    }

    function addFailureMessage(error) {
      return describeAddError(error);
    }

    function showAddFailureToast(error) {
      if (error && error.isSecurityChallenge) {
        // theme-cart already raised the persistent challenge banner; avoid a duplicate toast.
        return;
      }
      if (window.IBCartNotice && window.IBCartNotice.toast) {
        window.IBCartNotice.toast(addFailureMessage(error));
      }
    }

    function cartStateFromPayload(payload) {
      if (!payload) return null;
      if (payload.items && payload.items.length != null) return payload;
      if (payload.cart && payload.cart.items && payload.cart.items.length != null) return payload.cart;
      if (payload.cartState && payload.cartState.items && payload.cartState.items.length != null) return payload.cartState;
      var cart = window.theme && window.theme.cart;
      if (cart && cart.getCartState) {
        try {
          var state = cart.getCartState(payload);
          if (state && state.items && state.items.length != null) return state;
        } catch (e) {}
      }
      return null;
    }

    function readLiveCart() {
      var cart = window.theme && window.theme.cart;
      if (cart) {
        if (cart.currentCart && cart.currentCart.items) return Promise.resolve(cart.currentCart);
        if (cart.cart && cart.cart.items) return Promise.resolve(cart.cart);
        if (cart.getCart) return cart.getCart();
      }
      if (typeof fetch !== 'function') return Promise.resolve(null);
      return fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error('cart ' + res.status);
        return res.json();
      });
    }

    function promoPropertyMatches(item, promoId) {
      var props = item && item.properties;
      return !!(props && props._promo_id != null && String(props._promo_id) === String(promoId));
    }

    function findVariantPromoLine(cart, variantId, promoId) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].variant_id) === String(variantId) && promoPropertyMatches(items[i], promoId)) {
          return items[i];
        }
      }
      return null;
    }

    function syncVariantListQuantities(scope, promoId, cart) {
      if (!scope || !cart || !cart.items) return;
      $all('.cg-qty[data-atc-external]', scope).forEach(function (q) {
        if (q._spPending || q._spEditing) return;
        var input = $('.cg-qty-input', q);
        var variantId = parseInt(q.getAttribute('data-variant-id'), 10);
        if (!input || !variantId) return;
        var line = findVariantPromoLine(cart, variantId, promoId);
        input.value = String(line ? (parseInt(line.quantity, 10) || 0) : 0);
      });
    }

    function syncVariantListFromLiveCart(scope, promoId) {
      return readLiveCart().then(function (cart) {
        syncVariantListQuantities(scope, promoId, cart);
        return cart;
      }).catch(function () { return null; });
    }

    function addScopeEntry(entries, scope, promoId) {
      if (!scope || promoId == null) return;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].scope === scope) return;
      }
      entries.push({ scope: scope, promoId: promoId });
    }

    function registerVariantScope(scope, promoId) {
      if (!scope || promoId == null) return;
      for (var i = 0; i < variantScopes.length; i++) {
        if (variantScopes[i].scope === scope) {
          variantScopes[i].promoId = promoId;
          return;
        }
      }
      variantScopes.push({ scope: scope, promoId: promoId });
    }

    function syncOpenVariantLists(cart) {
      var entries = [];
      // Prune scopes whose DOM is gone (closed modals) so the registry can't
      // grow unboundedly; keep entries where isConnected is undefined (test fakes).
      variantScopes = variantScopes.filter(function (entry) {
        return !(entry.scope && entry.scope.isConnected === false);
      });
      if (typeof document !== 'undefined' && document && document.querySelectorAll) {
        $all('.rs-vsec[data-sp-vlist-promo]', document).forEach(function (scope) {
          addScopeEntry(entries, scope, scope.getAttribute('data-sp-vlist-promo'));
        });
      }
      variantScopes.forEach(function (entry) {
        addScopeEntry(entries, entry.scope, entry.promoId);
      });
      entries.forEach(function (entry) {
        syncVariantListQuantities(entry.scope, entry.promoId, cart);
      });
    }

    function bindVariantCartSync() {
      if (variantCartSyncBound) return;
      variantCartSyncBound = true;
      if (typeof document === 'undefined' || !document || !document.addEventListener) return;
      function sync(evt) {
        var cart = cartStateFromPayload(evt && evt.detail);
        if (cart) { syncOpenVariantLists(cart); return; }
        readLiveCart().then(function (liveCart) { syncOpenVariantLists(liveCart); }).catch(function () {});
      }
      document.addEventListener('cart:updated', sync);
      document.addEventListener('cart:build', sync);
    }

    function bind(scope, promo) {
      if (!scope || !promo) return;
      var promoId = promo.id;
      registerVariantScope(scope, promoId);
      bindVariantCartSync();
      $all('.cg-qty[data-atc-external]', scope).forEach(function (q) {
        if (q._spVbound) return;
        q._spVbound = true;
        var input = $('.cg-qty-input', q);
        var dec = $('.cg-qty-btn--minus', q);
        var inc = $('.cg-qty-btn--plus', q);
        function set(v) { if (input) input.value = String(Math.max(0, Math.min(99, v))); }
        function cur() { return input ? (parseInt(input.value, 10) || 0) : 0; }
        function variantId() { return parseInt(q.getAttribute('data-variant-id'), 10); }
        function cartApi() { return window.theme && window.theme.cart; }
        function blocked(cart) { return !cart || (cart.isChallengeActive && cart.isChallengeActive()); }
        function settle(result) {
          q._spPending = false;
          var cartState = cartStateFromPayload(result);
          if (cartState) syncVariantListQuantities(scope, promoId, cartState);
          else syncVariantListFromLiveCart(scope, promoId);
        }
        function fail(previous, error) {
          q._spPending = false;
          set(previous);
          showAddFailureToast(error);
        }
        function quantityFromResult(result, fallback) {
          var cartState = cartStateFromPayload(result);
          var line = cartState ? findVariantPromoLine(cartState, variantId(), promoId) : null;
          return line ? (parseInt(line.quantity, 10) || 0) : fallback;
        }
        function addFail(previous, error) {
          q._spQueuedAdds = 0;
          fail(previous, error);
        }
        function dropAdd(previous) {
          q._spQueuedAdds = 0;
          q._spPending = false;
          set(previous);
        }
        function runAdd(n, previous) {
          var cart = cartApi();
          var id = variantId();
          if (blocked(cart) || !id || !cart.addSilently) {
            dropAdd(previous);
            return;
          }
          cart.addSilently([{ id: id, quantity: n, properties: { _promo_id: promo.id } }],
            { source: 'shoppable-promo-add' })
            .then(function (result) {
              if (q._spQueuedAdds > 0) {
                var queued = q._spQueuedAdds;
                q._spQueuedAdds = 0;
                q._spPending = true;
                runAdd(queued, quantityFromResult(result, previous + n));
                return;
              }
              settle(result);
            })
            .catch(function (error) { addFail(previous, error); });
        }
        if (input) {
          input.addEventListener('focus', function () { q._spEditing = true; });
          input.addEventListener('blur', function () {
            q._spEditing = false;
            syncVariantListFromLiveCart(scope, promoId);
          });
          input.addEventListener('change', function () { set(cur()); });
        }
        if (dec) dec.addEventListener('click', function () {
          if (q._spPending) return;
          var previous = cur();
          var cart = cartApi();
          if (previous <= 0 || blocked(cart)) return;
          var id = variantId();
          if (!id || !cart.changeLineAndBroadcast) return;
          set(previous - 1);
          q._spPending = true;
          readLiveCart().then(function (liveCart) {
            var line = findVariantPromoLine(liveCart, id, promoId);
            if (!line) {
              q._spPending = false;
              syncVariantListQuantities(scope, promoId, liveCart);
              return null;
            }
            // Decrement from the AUTHORITATIVE live line quantity (not the optimistic
            // input, which can be stale if the cart changed out of band). changeLine
            // takes an absolute quantity; 0 removes the line.
            var nextQty = Math.max(0, (parseInt(line.quantity, 10) || 0) - 1);
            return cart.changeLineAndBroadcast(line.key, nextQty, { source: 'shoppable-promo-add' })
              .then(settle);
          }).catch(function (error) { fail(previous, error); });
        });
        if (inc) inc.addEventListener('click', function () {
          var previous = cur();
          if (q._spPending) {
            q._spQueuedAdds = (q._spQueuedAdds || 0) + 1;
            set(previous + 1);
            return;
          }
          var cart = cartApi();
          var id = variantId();
          if (blocked(cart) || !id || !cart.addSilently) return;
          set(previous + 1);
          q._spPending = true;
          runAdd(1, previous);
        });
      });
      syncVariantListFromLiveCart(scope, promoId);
    }

    return {
      bind: bind,
      sync: syncVariantListQuantities,
      syncFromLiveCart: syncVariantListFromLiveCart,
      addErrorText: addErrorText,
      addErrorDescription: addErrorDescription,
      addFailureMessage: addFailureMessage,
      showAddFailureToast: showAddFailureToast
    };
  })();

  Engine.prototype.variantListHtml = function (product, promo) {
    var rows = (product.variants || []).map(function (v) {
      var avail = v.available !== false;
      var cmp = v.compare_at_price || 0;
      var priceHtml = '<span class="rs-vrow-now">' + escapeHtml(formatMoney(v.price)) + '</span>' +
        (cmp > v.price ? '<s class="rs-vrow-was">' + escapeHtml(formatMoney(cmp)) + '</s>' : '');
      var stepper = avail
        ? '<div class="cg-actions"><div class="cg-qty" data-atc-external="1" data-variant-id="' + v.id +
          '" data-sku="' + escapeHtml(v.sku || '') + '" data-available="true">' +
          '<button class="cg-qty-btn cg-qty-btn--minus" type="button" aria-label="Decrease quantity">' + SVG.minus + '</button>' +
          '<input class="cg-qty-input" type="number" value="0" min="0" max="99" readonly aria-label="Quantity">' +
          '<button class="cg-qty-btn cg-qty-btn--plus" type="button" aria-label="Increase quantity">' + SVG.inc + '</button>' +
          '</div></div>'
        : '<span class="cg-sold-out">Sold out</span>';
      return '<div class="rs-vrow' + (avail ? '' : ' rs-vrow--out') + '">' +
        '<div class="rs-vrow-main"><span class="rs-vrow-name">' + escapeHtml(v.title) + '</span>' +
        (v.sku ? '<span class="rs-vrow-sku">' + escapeHtml(v.sku) + '</span>' : '') + '</div>' +
        '<div class="rs-vrow-price">' + priceHtml + '</div>' + stepper + '</div>';
    }).join('');
    return '<div class="rs-sec rs-vsec" data-sp-vlist-promo="' + escapeHtml(promo.id) + '">' +
      '<div class="rs-sec-h"><span class="bar"></span>Choose your options' +
      '<span class="count">' + (product.variants || []).length + ' options</span></div>' +
      '<div class="rs-vlist">' + rows + '</div></div>';
  };

  Engine.prototype.bindVariantList = function (scope, promo) {
    return PromoVariantCart.bind(scope, promo);
  };

  // M3: lazy-fill the "N items / products" count after the grid fetch.
  Engine.prototype.fillGridCount = function (gridEl, total) {
    var modal = gridEl.closest ? gridEl.closest('.rs-modal') : null;
    if (!modal) return;
    var countEl = $('[data-sp-grid-count]', modal);
    if (countEl) countEl.textContent = total + ' product' + (total === 1 ? '' : 's');
  };

  // 404 / empty -> fall back to the safe link-out (spec §5 step 4).
  Engine.prototype.gridFallback = function (gridEl, resolved, message) {
    var href = escapeHtml(safeHref(resolved.fallbackLink || '#'));
    gridEl.innerHTML = '<div class="sp-status">' +
      '<div class="sp-status__title">' + escapeHtml(message) + '</div>' +
      '<a class="sp-retry-btn" href="' + href + '">View on the site</a></div>';
  };

  // Rebind injected collection quick-add markup through CollectionATC's public facade.
  Engine.prototype.reinitQuickAdd = function (scope) {
    var CA = window.CollectionATC;
    if (CA && CA.rebind) CA.rebind(scope);
  };

  /* ---------- Modal a11y helper for floating brand modal ---------- */
  Engine.prototype.trapModal = function (overlay, onClose) {
    // Remember the trigger so focus returns to it on close (the detail modal's
    // closeModal restores this.lastFocus; floating overlays must too, else focus
    // is lost to <body> after auto-focusing the first input below).
    overlay._spLastFocus = document.activeElement;
    var keyHandler = function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if (e.key === 'Tab') {
        var f = $all('button, select, input, a[href], [tabindex]:not([tabindex="-1"])', overlay)
          .filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', keyHandler, true);
    overlay._spKeyHandler = keyHandler;
    var firstInput = $('input, button', overlay);
    if (firstInput) firstInput.focus();
  };

  Engine.prototype.closeFloating = function (overlay) {
    if (overlay._spKeyHandler) {
      document.removeEventListener('keydown', overlay._spKeyHandler, true);
      overlay._spKeyHandler = null;
    }
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    // Restore focus to the element that opened the overlay (a11y).
    if (overlay._spLastFocus && overlay._spLastFocus.focus) overlay._spLastFocus.focus();
    overlay._spLastFocus = null;
  };

  /* ----------------------------------------------------------------------
   * 12. Bootstrap (defer-safe)
   * -------------------------------------------------------------------- */
  function boot() {
    var roots = $all('.shoppable-promotions');
    roots.forEach(function (root) {
      if (root.getAttribute('data-sp-booted')) return;
      root.setAttribute('data-sp-booted', '1');
      var engine = new Engine(root);
      // Expose the most-recent engine for debugging / tests.
      window.ShoppablePromotions._engine = engine;
      if (!feedReady) { engine.renderError(MISSING_FEED_MESSAGE); return; }
      engine.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    // Defer to the next tick so `window.ShoppablePromotions` is assigned (this
    // IIFE has returned) BEFORE boot() references it at `._engine = engine`.
    // Required under defer, where readyState is already 'interactive' here, so a
    // synchronous boot() would throw and abort the whole module assignment.
    setTimeout(boot, 0);
  }
  document.addEventListener('shopify:section:load', function (e) {
    var target = e && e.target;
    if (!target || (target.matches && target.matches('.shoppable-promotions')) ||
        (target.querySelector && target.querySelector('.shoppable-promotions'))) {
      boot();
    }
  });

  // ---- Preview exports (used by promo-feed-v2-reference.html) -------------
  // Render a single RAW v2 promo to its card / modal HTML exactly as the live
  // section would, so the reference tool's preview stays in lockstep with the
  // engine (no duplicated rendering logic). Gating is driven by whatever
  // window.IBAccessPolicy the host page provides (the tool stubs it per the
  // customer-state toggle); with no policy, evaluatePromo fails open.
  function previewCfg(opts) {
    opts = opts || {};
    return {
      density: opts.density || 'regular',
      badgeStyle: opts.badgeStyle || 'medium',
      grouping: 'flat',
      spotlight: opts.spotlight !== false,
      urgency: opts.urgency || 'countdown',
      pricingRestrictionsEnabled: !!opts.pricingRestrictionsEnabled,
      customer: opts.customerState || null,
      endpoints: { collection: 'shoppable-promo-collection' }
    };
  }
  function renderPreviewCard(rawPromo, opts) {
    var cfg = previewCfg(opts);
    var p = normalizeFeed({ promotions: [rawPromo] })[0];
    if (!p) return '';
    var access = evaluatePromo(p, cfg.customer, cfg);
    return cardHtml(p, p.source, access, 0, cfg);
  }
  function renderPreviewModal(rawPromo, opts) {
    var cfg = previewCfg(opts);
    var p = normalizeFeed({ promotions: [rawPromo] })[0];
    if (!p) return '';
    var eng = Object.create(Engine.prototype);
    eng.cfg = cfg;
    return p.source.type === 'product' ? eng.productModalHtml(p) : eng.collectionModalHtml(p);
  }

  // Public surface (also lets tests vm-slice these by name).
  return {
    boot: boot,
    Engine: Engine,
    PromoVariantCart: PromoVariantCart,
    resolveSource: resolveSource,
    normalizeFeed: normalizeFeed,
    renderPreviewCard: renderPreviewCard,
    renderPreviewModal: renderPreviewModal,
    cleanFraming: cleanFraming,
    stripDollars: stripDollars,
    badgeText: badgeText,
    framingText: framingText,
    escapeHtml: escapeHtml,
    safeHref: safeHref,
    richText: richText,
    getPromotionCategories: getPromotionCategories,
    isPromoLive: isPromoLive,
    urgencyChipHtml: urgencyChipHtml,
    includedNoteQty: includedNoteQty,
    titleClass: titleClass,
    _engine: null
  };
})();
